import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import { useAuthGate } from '../Context/AuthGateContext';
import { useOfferSubscription } from '../hooks/useOfferSubscription';
import AdvancedFilters from './Marketplace/AdvancedFilters';
import Portfolio from './Portfolio/Portfolio';
import CommunityFeed from './Community/CommunityFeed';
import { FiSearch, FiGlobe, FiShoppingCart, FiDollarSign, FiCreditCard, FiLink, FiChevronDown, FiTrendingUp, FiZap, FiStar, FiUsers, FiShield, FiAlertTriangle, FiShare2, FiCheck, FiSend, FiPieChart, FiRadio, FiGrid, FiMessageCircle, FiInfo, FiSmartphone, FiHelpCircle } from 'react-icons/fi';
import { FaStar, FaPaypal, FaUniversity } from 'react-icons/fa';
import 'flag-icons/css/flag-icons.min.css';
import './Marketplace.css';
import { Avatar } from './Achievements/Achievements';

import { apiFetch } from '../services/apiClient';

// Pestañas del Marketplace ←→ URL (para que la ruta refleje dónde está el usuario)
const TAB_BY_PATH = { '/precios': 'market', '/muro': 'community' };
const PATH_BY_TAB = { offers: '/', market: '/precios', community: '/muro', portfolio: '/portfolio' };

const CHAIN_ICONS = {
  Bitcoin:  '/assets/crypto-logos/btc.svg',
  Ethereum: '/assets/crypto-logos/eth.svg',
  BNB:      '/assets/crypto-logos/bnb.svg',
  BSC:      '/assets/crypto-logos/bnb.svg',
  Solana:   '/assets/crypto-logos/sol.svg',
  Litecoin: '/assets/crypto-logos/ltc.svg',
  TRON:     '/assets/crypto-logos/trx.svg',
};

// Punto de color por red (reemplaza emojis decorativos por data-viz)
const CHAIN_COLORS = {
  Bitcoin: '#f7931a', Ethereum: '#627eea', BNB: '#f0b90b', BSC: '#f0b90b',
  Solana: '#9945ff', Litecoin: '#bfbbbb', TRON: '#ef0027',
};

// Red/blockchain → símbolo de moneda pixel (BSC→BNB, TRON→TRX, etc.)
function chainCoin(name) {
  const n = String(name || '').toLowerCase();
  if (/bitcoin|btc/.test(n))       return 'BTC';
  if (/ethereum|eth|erc/.test(n))  return 'ETH';
  if (/bsc|bnb|binance/.test(n))   return 'BNB';
  if (/solana|sol/.test(n))        return 'SOL';
  if (/litecoin|ltc/.test(n))      return 'LTC';
  if (/tron|trx|trc/.test(n))      return 'TRX';
  if (/tether|usdt/.test(n))       return 'USDT';
  if (/usdc/.test(n))              return 'USDC';
  return null;
}

// Logo real de cripto (PNG) con respaldo a punto de color con la inicial.
// Reemplaza al pixel-art: las monedas se muestran con su marca reconocible.
function CoinLogo({ symbol, size = 22, className = '' }) {
  const sym   = String(symbol || '').toUpperCase();
  const src   = CRYPTO_ICONS[sym];
  const color = CRYPTO_COLORS[sym] || 'var(--mk-text-tertiary)';
  return (
    <span className={`mk-coin-logo ${className}`} style={{ width: size, height: size }} title={sym}>
      {src && (
        <img src={src} alt={sym} width={size} height={size}
          onError={e => { e.currentTarget.style.display = 'none'; const f = e.currentTarget.nextSibling; if (f) f.style.display = 'flex'; }} />
      )}
      <span className="mk-coin-logo-fb" style={{ display: src ? 'none' : 'flex', background: color, width: size, height: size }}>
        {sym[0] || '?'}
      </span>
    </span>
  );
}

// Glifo de red: logo de la cripto si la red mapea, si no el punto de color
function ChainGlyph({ chain, px = 1 }) {
  const coin = chainCoin(chain);
  if (coin) return <CoinLogo symbol={coin} size={px >= 2 ? 18 : 14} className="mk-chain-logo" />;
  return <span className="mk-chain-dot" style={{ background: CHAIN_COLORS[chain] ?? 'var(--mk-text-tertiary)' }} aria-hidden="true" />;
}

function ChainDot({ chain }) {
  return <ChainGlyph chain={chain} px={1} />;
}

// ===========================
// STABLECOINS — el núcleo educativo: empujar a USDT/USDC
// ===========================
const STABLES = new Set(['USDT', 'USDC']);
const isStable = (sym) => STABLES.has(String(sym || '').toUpperCase());

// Textos cortos reutilizables para los tooltips "¿qué es esto?"
const EDU = {
  trader:    'El anunciante: la persona con la que vas a operar. Mirá su reputación (trades, rating, % de finalización) antes de aceptar.',
  type:      'Compra = el anunciante quiere comprar cripto (vos le vendés). Venta = el anunciante vende cripto (vos le comprás).',
  available: 'Cuánta cripto tiene disponible esta oferta y los límites por operación (mínimo–máximo que podés negociar de una vez).',
  chain:     'La red blockchain por la que viaja la cripto (Bitcoin, Tron, BNB…). Cada red tiene su propia comisión y velocidad. USDT y USDC existen en varias redes.',
  payment:   'Cómo pagás o cobrás el fiat (SINPE, Nequi, transferencia, PayPal…). Elegí un método que ambos tengan.',
  price:     'Precio por USDT. Premium (+%) = pagás más que el mercado. Descuento (−%) = pagás menos. Ejemplo: −1% → 100 USDT cuestan $99; +1% → cuestan $101. TRM = precio de mercado, sin recargo.',
  resp:      'Tiempo promedio que tarda el anunciante en responder y liberar la cripto. Menos minutos = más ágil.',
  trust:     'Puntaje de confianza (0–100) combinando rating, cantidad de trades y velocidad. Verde = muy confiable. También ves el % de operaciones completadas.',
  escrow:    'Hoy operás P2P directo: tu plata vive en tu wallet y el vendedor controla la suya — Bitplify NUNCA las toca. La protección es curación (vendedores verificados), reputación y disputas. El escrow 2-de-3 (nadie mueve los fondos solo) llega en una fase futura.',
  stable:    'Una stablecoin (USDT, USDC) vale siempre ≈ 1 dólar. No sube ni baja como Bitcoin: ideal para guardar y enviar valor sin sorpresas.',
};

// Tooltip "¿qué es esto?" — ⓘ con popover (hover en desktop, tap en mobile)
function InfoTip({ text, label = 'Qué es esto', className = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <span className={`mk-infotip ${open ? 'open' : ''} ${className}`} ref={ref}>
      <button type="button" className="mk-infotip-btn" aria-label={label}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(o => !o); }}>
        <FiInfo />
      </button>
      <span className="mk-infotip-pop" role="tooltip">{text}</span>
    </span>
  );
}

// ===========================
// MONEDA LOCAL — precio de referencia en la moneda del país del anunciante
// ===========================
const COUNTRY_CURRENCY = {
  CR: { code: 'CRC', symbol: '₡' },  CO: { code: 'COP', symbol: '$' },
  VE: { code: 'VES', symbol: 'Bs' }, MX: { code: 'MXN', symbol: '$' },
  AR: { code: 'ARS', symbol: '$' },  PE: { code: 'PEN', symbol: 'S/' },
  CL: { code: 'CLP', symbol: '$' },  BR: { code: 'BRL', symbol: 'R$' },
  BO: { code: 'BOB', symbol: 'Bs' }, PY: { code: 'PYG', symbol: '₲' },
  UY: { code: 'UYU', symbol: '$U' }, EC: { code: 'USD', symbol: '$' },
  PA: { code: 'USD', symbol: '$' },  US: { code: 'USD', symbol: '$' },
  ES: { code: 'EUR', symbol: '€' },
};

// Tasas fiat USD→* (open.er-api.com, sin API key). Singleton + caché localStorage (6 h).
let _fiatRates = null;
let _fiatPromise = null;
function fetchFiatRates() {
  if (_fiatRates) return Promise.resolve(_fiatRates);
  if (_fiatPromise) return _fiatPromise;
  try {
    const cached = JSON.parse(localStorage.getItem('mk_fiatRates') || 'null');
    if (cached?.rates && Date.now() - cached.t < 6 * 3600 * 1000) { _fiatRates = cached.rates; return Promise.resolve(_fiatRates); }
  } catch {}
  _fiatPromise = fetch('https://open.er-api.com/v6/latest/USD')
    .then(r => r.json())
    .then(d => {
      _fiatRates = d?.rates || null;
      if (_fiatRates) { try { localStorage.setItem('mk_fiatRates', JSON.stringify({ t: Date.now(), rates: _fiatRates })); } catch {} }
      return _fiatRates;
    })
    .catch(() => null);
  return _fiatPromise;
}
function useFiatRates() {
  const [rates, setRates] = useState(_fiatRates);
  useEffect(() => {
    if (_fiatRates) { setRates(_fiatRates); return undefined; }
    let alive = true;
    fetchFiatRates().then(r => { if (alive) setRates(r); });
    return () => { alive = false; };
  }, []);
  return rates;
}
// Tasa PARALELA (la real de mercado, no la oficial) para países con brecha cambiaria.
// ARS = dólar blue, VES = paralelo (dolarapi). Singleton + caché 3 h.
let _parRates = null;
let _parPromise = null;
function fetchParallelRates() {
  if (_parRates) return Promise.resolve(_parRates);
  if (_parPromise) return _parPromise;
  try {
    const cached = JSON.parse(localStorage.getItem('mk_parRates') || 'null');
    if (cached?.rates && Date.now() - cached.t < 3 * 3600 * 1000) { _parRates = cached.rates; return Promise.resolve(_parRates); }
  } catch {}
  _parPromise = Promise.all([
    fetch('https://dolarapi.com/v1/dolares/blue').then(r => r.json()).catch(() => null),
    fetch('https://ve.dolarapi.com/v1/dolares/paralelo').then(r => r.json()).catch(() => null),
  ]).then(([ar, ve]) => {
    const rates = {};
    if (ar?.venta)    rates.ARS = ar.venta;
    if (ve?.promedio) rates.VES = ve.promedio;
    _parRates = Object.keys(rates).length ? rates : null;
    if (_parRates) { try { localStorage.setItem('mk_parRates', JSON.stringify({ t: Date.now(), rates: _parRates })); } catch {} }
    return _parRates;
  }).catch(() => null);
  return _parPromise;
}
function useParallelRates() {
  const [rates, setRates] = useState(_parRates);
  useEffect(() => {
    if (_parRates) { setRates(_parRates); return undefined; }
    let alive = true;
    fetchParallelRates().then(r => { if (alive) setRates(r); });
    return () => { alive = false; };
  }, []);
  return rates;
}

// Precio (USD por USDT) → { str, parallel } en moneda local. Prefiere la tasa paralela.
// null si el país usa USD o no hay tasa.
function localPrice(usdPrice, country, rates, parRates) {
  const cur = COUNTRY_CURRENCY[country];
  if (!cur || cur.code === 'USD') return null;
  let rate = null, parallel = false;
  if (parRates && parRates[cur.code])    { rate = parRates[cur.code]; parallel = true; }
  else if (rates && rates[cur.code])     { rate = rates[cur.code]; }
  if (!rate) return null;
  const val = usdPrice * rate;
  return { str: `${cur.symbol}${val.toLocaleString('es', { maximumFractionDigits: val >= 100 ? 0 : 2 })}`, parallel };
}

// Presencia del trader (online/offline) — determinística por nombre (mock hasta tener backend)
function traderPresence(offer) {
  const h = repHash(String(offer.trader || '') + '#p');
  const online = h % 3 !== 0;             // ~66% en línea
  const mins = (h % 55) + 4;
  return { online, lastSeen: online ? 'En línea' : (mins < 60 ? `hace ${mins} min` : `hace ${Math.round(mins / 60)} h`) };
}

// Agrupación de métodos de pago por categoría (para el <select> del filtro)
const PAY_KIND_LABEL = { mobile: 'Billeteras móviles', bank: 'Transferencias y bancos', send: 'Apps de pago / envío', crypto: 'Cripto', card: 'Otros' };
const PAY_KIND_ORDER = ['mobile', 'bank', 'send', 'crypto', 'card'];
function groupPayments(list) {
  const groups = {};
  (list || []).forEach(p => {
    if (p === 'Todos') return;
    const { kind } = paymentMeta(p);
    (groups[kind] = groups[kind] || []).push(p);
  });
  return PAY_KIND_ORDER.filter(k => groups[k]?.length).map(k => [k, groups[k]]);
}

// ===========================
// CENTRO EDUCATIVO — glosario + cómo funciona + evitar estafas
// ===========================
const GLOSSARY = [
  { t: 'Stablecoin (USDT / USDC)', d: 'Cripto que vale siempre ≈ 1 dólar. No sube ni baja como Bitcoin: ideal para guardar y enviar valor sin sorpresas.' },
  { t: 'P2P (persona a persona)',  d: 'Comprás y vendés directo con otra persona. Ningún exchange fija el precio: lo acordás con tu contraparte.' },
  { t: 'Protección del trade',     d: 'Hoy: vendedores verificados por invitación + reputación + disputas con evidencia. Tu plata vive en tu wallet, Bitplify no la custodia. El escrow 2-de-3 (nadie mueve los fondos solo) llega en una fase futura.' },
  { t: 'Premium / Descuento',      d: 'Cuánto se aleja el precio del mercado. +1% premium → 100 USDT cuestan $101. −1% descuento → cuestan $99. Más bajo es mejor si comprás.' },
  { t: 'TRM / precio de mercado',  d: 'El valor de referencia del mercado, sin recargo. Una oferta "TRM" opera a ese precio (spread 0%).' },
  { t: 'Red / Blockchain',         d: 'El "carril" por el que viaja la cripto (Bitcoin, Tron, BNB…). Cada red tiene su comisión y velocidad. USDT existe en varias.' },
  { t: 'Tasa paralela',            d: 'En países con control cambiario (Venezuela, Argentina) el dólar real de mercado difiere del oficial. Mostramos el paralelo donde aplica.' },
  { t: 'Verificación (KYC)',       d: 'Confirmar tu identidad. En Bitplify, vender requiere ser comerciante verificado por invitación.' },
];
const SCAM_TIPS = [
  { t: 'Esperá el dinero, no el comprobante', d: 'Un comprobante puede ser falso o editado. Liberá la cripto solo cuando el dinero esté acreditado EN tu cuenta.' },
  { t: 'El pago debe venir del comprador',    d: 'Rechazá pagos de terceros (otro nombre). Si la cuenta que paga no coincide con tu contraparte, no liberes.' },
  { t: 'Cuidado con métodos reversibles',     d: 'PayPal, tarjeta o Mercado Pago se pueden revertir (contracargo). Preferí métodos irreversibles: SINPE, transferencia, PIX.' },
  { t: 'Nunca salgas de la plataforma',       d: 'Si te ofrecen "mejor precio" por WhatsApp/Telegram por fuera de Bitplify, es estafa. Todo —chat, comprobantes y disputas— tiene que quedar dentro de la plataforma.' },
  { t: 'El "sobrepago por error" es fraude',  d: 'Si "pagan de más" y piden que devuelvas la diferencia, es un engaño clásico. No devuelvas nada por fuera del trade.' },
];

function EduCenter({ onClose, initialTab = 'como' }) {
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="mk-modal-overlay" onClick={onClose}>
      <div className="mk-edu-modal" onClick={e => e.stopPropagation()}>
        <div className="mk-edu-modal-head">
          <h3><FiHelpCircle className="mk-edu-modal-ic" /> Aprendé a operar seguro</h3>
          <button className="mk-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mk-edu-tabs">
          <button className={tab === 'como' ? 'active' : ''} onClick={() => setTab('como')}>Cómo funciona</button>
          <button className={tab === 'glos' ? 'active' : ''} onClick={() => setTab('glos')}>Glosario</button>
          <button className={tab === 'estafa' ? 'active' : ''} onClick={() => setTab('estafa')}>Evitar estafas</button>
        </div>
        <div className="mk-edu-modal-body">
          {tab === 'como' && (
            <>
              <ol className="mk-edu-flow">
                <li><span className="mk-edu-num">1</span><div><strong>Elegí una oferta</strong><p>Filtrá por método de pago, país y precio. Empezá en USDT/USDC (estables, ≈ $1).</p></div></li>
                <li><span className="mk-edu-num">2</span><div><strong>Se abre el trade con un vendedor verificado</strong><p>Coordinás por el chat. Tu plata y la del vendedor siempre bajo el control de cada uno — <strong>Bitplify no las toca</strong>.</p></div></li>
                <li><span className="mk-edu-num">3</span><div><strong>Pagás por tu método</strong><p>Hacés el pago fiat (SINPE, transferencia, etc.) y marcás "pagado" con el comprobante.</p></div></li>
                <li><span className="mk-edu-num">4</span><div><strong>Recibís tu cripto</strong><p>Cuando el vendedor confirma que le llegó el dinero, te la envía a tu wallet. ¿Algo raro? Abrís una disputa con evidencia.</p></div></li>
              </ol>
              <div className="mk-edu-callout"><FiShield /> Tu protección hoy: <strong>vendedores verificados por invitación</strong>, reputación pública y disputas con evidencia. Tu plata vive en tu wallet — nadie la custodia.</div>
            </>
          )}
          {tab === 'glos' && (
            <div className="mk-edu-list">
              {GLOSSARY.map(g => <div key={g.t} className="mk-edu-item"><strong>{g.t}</strong><p>{g.d}</p></div>)}
            </div>
          )}
          {tab === 'estafa' && (
            <div className="mk-edu-list">
              <div className="mk-edu-callout warn"><FiAlertTriangle /> La mayoría de las estafas P2P se evitan con una regla: <strong>liberá la cripto solo cuando el dinero esté en tu cuenta.</strong></div>
              {SCAM_TIPS.map(s => <div key={s.t} className="mk-edu-item"><strong>{s.t}</strong><p>{s.d}</p></div>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Comerciantes favoritos (estilo "seguir" de Binance). localStorage + sync entre instancias.
const FAV_KEY = 'bitplify_fav_traders';
function readFavTraders() { try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || '[]')); } catch { return new Set(); } }
function useFavTraders() {
  const [favs, setFavs] = useState(readFavTraders);
  useEffect(() => {
    const h = () => setFavs(readFavTraders());
    window.addEventListener('bitplify-favs', h);
    return () => window.removeEventListener('bitplify-favs', h);
  }, []);
  const toggleFav = useCallback((name) => {
    const next = readFavTraders();
    if (next.has(name)) next.delete(name); else next.add(name);
    localStorage.setItem(FAV_KEY, JSON.stringify([...next]));
    window.dispatchEvent(new Event('bitplify-favs'));
  }, []);
  return [favs, toggleFav];
}

// ✅ Lista de cryptos para el tab Mercado
const CRYPTO_MARKET_LIST = [
  { symbol: 'BTCUSDT', name: 'Bitcoin',  short: 'BTC', color: '#f7931a', logo: '/assets/crypto-logos/btc.svg'  },
  { symbol: 'ETHUSDT', name: 'Ethereum', short: 'ETH', color: '#627eea', logo: '/assets/crypto-logos/eth.svg' },
  { symbol: 'BNBUSDT', name: 'BNB',      short: 'BNB', color: '#f0b90b', logo: '/assets/crypto-logos/bnb.svg'      },
  { symbol: 'SOLUSDT', name: 'Solana',   short: 'SOL', color: '#9945ff', logo: '/assets/crypto-logos/sol.svg'   },
  { symbol: 'ADAUSDT', name: 'Cardano',  short: 'ADA', color: '#0033ad', logo: '/assets/crypto-logos/ada.svg' },
  { symbol: 'XRPUSDT', name: 'Ripple',   short: 'XRP', color: '#23292f', logo: '/assets/crypto-logos/xrp.svg' },
  { symbol: 'SUIUSDT', name: 'Sui',      short: 'SUI', color: '#4da2ff', logo: '/assets/crypto-logos/sui.svg' },
  { symbol: 'LTCUSDT', name: 'Litecoin', short: 'LTC', color: '#bfbbbb', logo: '/assets/crypto-logos/ltc.svg' },
  { symbol: 'TRXUSDT', name: 'TRON',     short: 'TRX', color: '#ef0027', logo: '/assets/crypto-logos/trx.svg'     },
];

const TIMEFRAMES = [
  { label: '1H', interval: '1m',  limit: 60  },
  { label: '4H', interval: '5m',  limit: 48  },
  { label: '1D', interval: '15m', limit: 96  },
  { label: '1W', interval: '1h',  limit: 168 },
];

const COUNTRIES = [
  { code: 'all', flag: '🌍', label: 'Global'          },
  { code: 'CR',  flag: '🇨🇷', label: 'Costa Rica'     },
  { code: 'CO',  flag: '🇨🇴', label: 'Colombia'       },
  { code: 'VE',  flag: '🇻🇪', label: 'Venezuela'      },
  { code: 'MX',  flag: '🇲🇽', label: 'México'         },
  { code: 'AR',  flag: '🇦🇷', label: 'Argentina'      },
  { code: 'PE',  flag: '🇵🇪', label: 'Perú'           },
  { code: 'CL',  flag: '🇨🇱', label: 'Chile'          },
  { code: 'BR',  flag: '🇧🇷', label: 'Brasil'         },
  { code: 'EC',  flag: '🇪🇨', label: 'Ecuador'        },
  { code: 'BO',  flag: '🇧🇴', label: 'Bolivia'        },
  { code: 'PY',  flag: '🇵🇾', label: 'Paraguay'       },
  { code: 'UY',  flag: '🇺🇾', label: 'Uruguay'        },
  { code: 'PA',  flag: '🇵🇦', label: 'Panamá'         },
  { code: 'US',  flag: '🇺🇸', label: 'Estados Unidos' },
  { code: 'ES',  flag: '🇪🇸', label: 'España'         },
];

// Bandera SVG (flag-icons) — rectangular y consistente en todos los SO.
// 'all'/sin código → globo de línea. Códigos ISO-3166 alpha-2 (los mismos de COUNTRIES).
function Flag({ code, className = '', title }) {
  if (!code || code === 'all') return <FiGlobe className={`mk-flag-globe ${className}`} title={title} />;
  return <span className={`fi fi-${code.toLowerCase()} mk-flag ${className}`} title={title} aria-hidden="true" />;
}

function getCountry(code) {
  return COUNTRIES.find(c => c.code === code) ?? { flag: '🌍', label: code };
}

const PAYMENTS_BY_COUNTRY = {
  all: ['Todos', 'Transferencia bancaria', 'PayPal', 'Wink', 'Nequi', 'Daviplata', 'Zinli', 'SINPE Móvil', 'Mercado Pago', 'PIX', 'Zelle', 'Bizum', 'Yape', 'Plin', 'SPEI', 'Khipu'],
  CR:  ['Todos', 'SINPE Móvil', 'Transferencia BAC', 'Transferencia Popular', 'Transferencia Nacional', 'Wink', 'PayPal'],
  CO:  ['Todos', 'Nequi', 'Daviplata', 'PSE', 'Bancolombia', 'Banco de Bogotá', 'Davivienda', 'Transferencia bancaria', 'PayPal'],
  VE:  ['Todos', 'Pago Móvil', 'Zinli', 'Zelle', 'PayPal', 'Binance Pay', 'Transferencia bancaria', 'Reserve', 'Mercantil'],
  MX:  ['Todos', 'SPEI', 'Mercado Pago', 'PayPal', 'BBVA', 'Banamex', 'Oxxo Pay', 'Transferencia bancaria', 'CoDi'],
  AR:  ['Todos', 'Mercado Pago', 'Transferencia bancaria', 'Naranja X', 'PayPal', 'Lemon Cash', 'Brubank', 'Uala'],
  PE:  ['Todos', 'Yape', 'Plin', 'BCP', 'Interbank', 'BBVA Perú', 'Scotiabank', 'PayPal', 'Transferencia bancaria'],
  CL:  ['Todos', 'Khipu', 'Transferencia bancaria', 'Mercado Pago', 'PayPal', 'Banco Estado', 'Santander CL', 'BCI'],
  BR:  ['Todos', 'PIX', 'Mercado Pago', 'PayPal', 'Nubank', 'Transferencia bancaria', 'PicPay', 'Itaú'],
  EC:  ['Todos', 'Transferencia bancaria', 'PayPal', 'Banco Pichincha', 'Produbanco', 'DeUna', 'Banco Guayaquil'],
  BO:  ['Todos', 'Transferencia bancaria', 'PayPal', 'Banco Bisa', 'BNB Bolivia', 'Tigo Money'],
  PY:  ['Todos', 'Transferencia bancaria', 'PayPal', 'Tigo Money PY', 'Personal Pay', 'Banco Continental'],
  UY:  ['Todos', 'Transferencia bancaria', 'PayPal', 'Mercado Pago UY', 'OCA', 'Abitab', 'RedPagos'],
  PA:  ['Todos', 'Transferencia bancaria', 'PayPal', 'Yappy', 'Banco General', 'Banistmo', 'Nequi PA'],
  US:  ['Todos', 'Zelle', 'Venmo', 'Cash App', 'PayPal', 'Wire Transfer', 'ACH', 'Apple Pay', 'Google Pay'],
  ES:  ['Todos', 'Bizum', 'Transferencia bancaria', 'PayPal', 'Revolut', 'SEPA', 'CaixaBank', 'Santander ES'],
};

// Mapeos para el deep-link desde la landing (bitplify.io → ?have=&method=)
const FIAT_TO_COUNTRY = { CRC: 'CR', COP: 'CO', VES: 'VE', MXN: 'MX', ARS: 'AR', PEN: 'PE' };
// Métodos del landing → nombres reales de pago en Costa Rica (base de usuarios)
const METHOD_TO_PAY = {
  sinpe: 'SINPE Móvil',
  bac: 'Transferencia BAC',
  nacional: 'Transferencia Nacional',
  popular: 'Transferencia Popular',
  wink: 'Wink',
  paypal: 'PayPal',
};

const MOCK_OFFERS = [
  { id: 'O-001', trader: 'DatuPanda',   country: 'CR', type: 'buy',  currency: 'ETH',  amount: 23535, blockchain: 'Ethereum', payment: 'SINPE Móvil',  responseTime: 3, spread: 0,    rating: 4.8, trades: 210, viewers: 4  },
  { id: 'O-002', trader: 'VIPER',        country: 'CO', type: 'sell', currency: 'BTC',  amount: 45454, blockchain: 'Bitcoin',  payment: 'Nequi',        responseTime: 2, spread: 2,    rating: 4.8, trades: 180, viewers: 7  },
  { id: 'O-003', trader: 'Chato',        country: 'VE', type: 'buy',  currency: 'BNB',  amount: 12234, blockchain: 'BNB',      payment: 'Pago Móvil',   responseTime: 4, spread: -2,   rating: 4.6, trades: 140, viewers: 2  },
  { id: 'O-004', trader: 'EddyInvestor', country: 'MX', type: 'sell', currency: 'SOL',  amount: 25234, blockchain: 'Solana',   payment: 'SPEI',         responseTime: 3, spread: 4,    rating: 4.8, trades: 200, viewers: 5  },
  { id: 'O-005', trader: 'Syl',          country: 'AR', type: 'buy',  currency: 'LTC',  amount: 95453, blockchain: 'Litecoin', payment: 'Mercado Pago', responseTime: 5, spread: -3,   rating: 4.5, trades: 110, viewers: 1  },
  { id: 'O-006', trader: 'JonDoe295',    country: 'PE', type: 'sell', currency: 'ETH',  amount: 23425, blockchain: 'Ethereum', payment: 'Yape',         responseTime: 2, spread: 5,    rating: 4.9, trades: 240, viewers: 9  },
  { id: 'O-007', trader: 'Eli',          country: 'CL', type: 'buy',  currency: 'BTC',  amount: 15000, blockchain: 'Bitcoin',  payment: 'Khipu',        responseTime: 6, spread: -1,   rating: 4.7, trades: 160, viewers: 3  },
  { id: 'O-008', trader: 'GaboNinja',    country: 'CO', type: 'sell', currency: 'BNB',  amount: 8500,  blockchain: 'BNB',      payment: 'Daviplata',    responseTime: 4, spread: 1.5,  rating: 4.7, trades: 170, viewers: 6  },
  { id: 'O-009', trader: 'CryptoLuna',   country: 'VE', type: 'buy',  currency: 'SOL',  amount: 32000, blockchain: 'Solana',   payment: 'Zinli',        responseTime: 8, spread: 0,    rating: 4.3, trades: 85,  viewers: 2  },
  { id: 'O-010', trader: 'BlockBoss',    country: 'US', type: 'sell', currency: 'ETH',  amount: 56000, blockchain: 'Ethereum', payment: 'Zelle',        responseTime: 1, spread: 3,    rating: 5.0, trades: 320, viewers: 12 },
  { id: 'O-011', trader: 'Rob',          country: 'BR', type: 'buy',  currency: 'BTC',  amount: 9800,  blockchain: 'Bitcoin',  payment: 'PIX',          responseTime: 3, spread: -0.5, rating: 4.6, trades: 130, viewers: 0  },
  { id: 'O-012', trader: 'BinanceHero',  country: 'US', type: 'sell', currency: 'LTC',  amount: 41200, blockchain: 'Litecoin', payment: 'Venmo',        responseTime: 2, spread: 2.5,  rating: 4.9, trades: 410, viewers: 8  },
  { id: 'O-013', trader: 'JP',           country: 'ES', type: 'sell', currency: 'ETH',  amount: 31000, blockchain: 'Ethereum', payment: 'Bizum',        responseTime: 2, spread: 1,    rating: 4.9, trades: 380, viewers: 6  },
  { id: 'O-014', trader: 'CryptoJuan',   country: 'CO', type: 'buy',  currency: 'BTC',  amount: 18000, blockchain: 'Bitcoin',  payment: 'PSE',          responseTime: 5, spread: -1.5, rating: 4.5, trades: 95,  viewers: 1  },
  { id: 'O-015', trader: 'WinkCR',       country: 'CR', type: 'sell', currency: 'USDT', amount: 5000,  blockchain: 'Solana',   payment: 'Wink',         responseTime: 3, spread: 0.5,  rating: 4.6, trades: 120, viewers: 3  },
];

const BLOCKCHAINS  = ['Todas', 'Bitcoin', 'Ethereum', 'BNB', 'Solana', 'Litecoin', 'TRON'];
const CRYPTOS      = ['Todas', 'BTC', 'ETH', 'BNB', 'SOL', 'LTC', 'TRX', 'USDT', 'USDC'];

// Logos + colores por símbolo de cripto (fallback a punto de color si no hay logo)
const CRYPTO_ICONS = {
  BTC: '/assets/crypto-logos/btc.svg',
  ETH: '/assets/crypto-logos/eth.svg',
  BNB: '/assets/crypto-logos/bnb.svg',
  SOL: '/assets/crypto-logos/sol.svg',
  LTC: '/assets/crypto-logos/ltc.svg',
  TRX: '/assets/crypto-logos/trx.svg',
  USDT: '/assets/crypto-logos/usdt.svg',
  USDC: '/assets/crypto-logos/usdc.svg',
  ADA: '/assets/crypto-logos/ada.svg',
  XRP: '/assets/crypto-logos/xrp.svg',
  SUI: '/assets/crypto-logos/sui.svg',
};
const CRYPTO_COLORS = { BTC: '#f7931a', ETH: '#627eea', BNB: '#f0b90b', SOL: '#9945ff', LTC: '#bfbbbb', TRX: '#ef0027', USDT: '#26a17b', USDC: '#2775ca', ADA: '#0033ad', XRP: '#23292f', SUI: '#4da2ff' };

// Estables primero (núcleo educativo: empujamos USDT/USDC), luego volátiles
const CRYPTO_OPTIONS = [
  { value: 'Todas', label: 'Todas las cripto', icon: null, color: '#5b6b7b', all: true, AllIcon: FiGrid },
  ...['USDT', 'USDC'].map(c => ({ value: c, label: c, icon: CRYPTO_ICONS[c] || null, color: CRYPTO_COLORS[c] || '#5b6b7b', coin: c, group: 'estables', stable: true })),
  ...['BTC', 'ETH', 'BNB', 'SOL', 'LTC', 'TRX'].map(c => ({ value: c, label: c, icon: CRYPTO_ICONS[c] || null, color: CRYPTO_COLORS[c] || '#5b6b7b', coin: c, group: 'volatiles' })),
];
const CHAIN_OPTIONS = BLOCKCHAINS.map(b => b === 'Todas'
  ? { value: 'Todas', label: 'Todas las redes', icon: null, color: '#5b6b7b', all: true, AllIcon: FiLink }
  : { value: b, label: b, icon: CHAIN_ICONS[b] || null, color: CHAIN_COLORS[b] || '#5b6b7b', coin: chainCoin(b) });

const SORT_OPTIONS = [
  { value: 'default', label: 'Por defecto'    },
  { value: 'spread',  label: 'Mejor precio'   },
  { value: 'speed',   label: 'Más rápido'     },
  { value: 'trust',   label: 'Mayor confianza' },
  { value: 'volume',  label: 'Mayor volumen'  },
];

const PAGE_SIZE = 11;

// ===========================
// SELECTOR DE ÍCONOS (cripto / blockchain) — estilo NoOnes
// ===========================
function IsThumb({ o }) {
  // "Todas" → ícono de línea limpio (no el blob gris tipo emoji)
  if (o.all) {
    const AllIcon = o.AllIcon || FiGrid;
    return (
      <span className="mk-isel-thumb all">
        <AllIcon className="mk-isel-allic" />
      </span>
    );
  }
  return (
    <span className="mk-isel-thumb">
      {o.icon && (
        <img src={o.icon} alt="" className="mk-isel-img"
          onError={e => { e.target.style.display = 'none'; const d = e.target.nextSibling; if (d) d.style.display = 'flex'; }} />
      )}
      <span className="mk-isel-dot" style={{ display: o.icon ? 'none' : 'flex', background: o.color }}>
        {o.label[0]}
      </span>
    </span>
  );
}

function IconSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const sel = options.find(o => o.value === value) || options[0];
  return (
    <div className="mk-isel" ref={ref}>
      <button type="button" className={`mk-isel-btn ${open ? 'open' : ''}`} onClick={() => setOpen(o => !o)}>
        <IsThumb o={sel} />
        <span className="mk-isel-val">{sel.label}</span>
        <FiChevronDown className="mk-isel-chev" />
      </button>
      {open && (
        <div className="mk-isel-menu">
          {options.map((o, i) => {
            const showHeader = o.group && o.group !== options[i - 1]?.group;
            return (
              <React.Fragment key={o.value}>
                {showHeader && (
                  <div className="mk-isel-group">
                    {o.group === 'estables' ? '✓ Monedas estables · recomendadas' : '⚡ Volátiles'}
                  </div>
                )}
                <button type="button" className={`mk-isel-opt ${o.value === value ? 'active' : ''}`}
                  onClick={() => { onChange(o.value); setOpen(false); }}>
                  <IsThumb o={o} />
                  <span className="mk-isel-optlabel">{o.label}{o.stable && <span className="mk-isel-badge">Estable</span>}</span>
                  {o.value === value && <FiCheck className="mk-isel-check" />}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===========================
// SPARKLINE
// ===========================
function Sparkline({ data, up }) {
  if (!data || data.length < 2) return null;
  const w = 80, h = 32, pad = 5;            // margen vertical: la línea nunca toca los bordes
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = pad + (h - pad * 2) * (1 - (v - min) / range);
    return `${x},${y}`;
  }).join(' ');
  const lineColor = up ? '#00d412' : '#f4511e';
  const gradId    = up ? 'spark-grad-up' : 'spark-grad-down';
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={lineColor} stopOpacity="0.22" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#${gradId})`} />
      <polyline points={pts} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ===========================
// CRYPTO CHART MODAL
// ===========================
function CryptoChart({ crypto, onClose, onTrade }) {
  const [tf, setTf]           = useState(1);
  const [candles, setCandles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const { interval, limit } = TIMEFRAMES[tf];
    fetch(`https://data-api.binance.vision/api/v3/klines?symbol=${crypto.symbol}&interval=${interval}&limit=${limit}`)
      .then(r => r.json())
      .then(data => {
        setCandles(data.map(c => ({ t: c[0], o: parseFloat(c[1]), h: parseFloat(c[2]), l: parseFloat(c[3]), c: parseFloat(c[4]), v: parseFloat(c[5]) })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [crypto.symbol, tf]);

  const prices    = candles.map(c => c.c);
  const min       = Math.min(...prices);
  const max       = Math.max(...prices);
  const range     = max - min || 1;
  const W = 480, H = 160;
  const up        = prices.length > 1 && prices[prices.length - 1] >= prices[0];
  const lineColor = up ? '#00d412' : '#f4511e';
  const pts       = prices.map((v, i) => {
    const x = (i / (prices.length - 1)) * W;
    const y = H - ((v - min) / range) * H * 0.85 - H * 0.075;
    return `${x},${y}`;
  }).join(' ');
  const pct = prices.length > 1 ? ((prices[prices.length-1] - prices[0]) / prices[0] * 100).toFixed(2) : '0.00';

  return (
    <div className="mk-chart-overlay" onClick={onClose}>
      <div className="mk-chart-modal" onClick={e => e.stopPropagation()}>
        <div className="mk-chart-header">
          <div className="mk-chart-title">
            <CoinLogo symbol={crypto.short} size={38} />
            <div>
              <div className="mk-chart-name">{crypto.name}</div>
              <div className="mk-chart-symbol">{crypto.short}/USDT</div>
            </div>
          </div>
          <button className="mk-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mk-chart-price-row">
          <div>
            <div className="mk-chart-current-price fin-num">
              ${prices.length ? prices[prices.length-1].toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
            </div>
            <div className={`mk-chart-change fin-num ${up ? 'up' : 'down'}`}>{up ? '▲' : '▼'} {Math.abs(pct)}% ({TIMEFRAMES[tf].label})</div>
          </div>
          <div className="mk-chart-tf-tabs">
            {TIMEFRAMES.map((t, i) => (
              <button key={t.label} className={`mk-chart-tf-btn ${tf === i ? 'active' : ''}`} onClick={() => setTf(i)}>{t.label}</button>
            ))}
          </div>
        </div>
        <div className="mk-chart-svg-wrap">
          {loading ? (
            <div className="mk-chart-loading">⏳ Cargando...</div>
          ) : (
            <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block' }}>
              <defs>
                <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={lineColor} stopOpacity="0.25" />
                  <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
                </linearGradient>
              </defs>
              {prices.length > 1 && (
                <>
                  <polygon points={`0,${H} ${pts} ${W},${H}`} fill="url(#chartGrad)" />
                  <polyline points={pts} fill="none" stroke={lineColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </>
              )}
            </svg>
          )}
        </div>
        <div className="mk-chart-stats">
          <div className="mk-chart-stat"><span className="fin-label">Máx</span><strong className="fin-num">${max.toLocaleString('en-US', { maximumFractionDigits: 2 })}</strong></div>
          <div className="mk-chart-stat"><span className="fin-label">Mín</span><strong className="fin-num">${min.toLocaleString('en-US', { maximumFractionDigits: 2 })}</strong></div>
          <div className="mk-chart-stat"><span className="fin-label">Vol 24h</span><strong className="fin-num">{candles.length ? (candles.reduce((s,c) => s + c.v, 0) / 1e6).toFixed(1) + 'M' : '—'}</strong></div>
        </div>
        <button className="mk-chart-trade-btn" onClick={() => onTrade(crypto.short)}>
          <FiShoppingCart className="mk-ic" /> Ver ofertas de {crypto.short} en el marketplace
        </button>
      </div>
    </div>
  );
}

// ===========================
// CRYPTO MARKET TAB
// ===========================
function CryptoMarket({ onTrade }) {
  const [prices, setPrices]     = useState({});
  const [prev, setPrev]         = useState({});
  const [sparks, setSparks]     = useState({});
  const [selected, setSelected] = useState(null);
  const [favs, setFavs]         = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('bitplify_watchlist') || '[]')); } catch { return new Set(); }
  });
  const [onlyFavs, setOnlyFavs] = useState(false);

  const toggleFav = (symbol) => {
    setFavs((prev) => {
      const next = new Set(prev);
      if (next.has(symbol)) next.delete(symbol); else next.add(symbol);
      localStorage.setItem('bitplify_watchlist', JSON.stringify([...next]));
      return next;
    });
  };

  const fetchPrices = useCallback(async () => {
    try {
      const symbols = CRYPTO_MARKET_LIST.map(c => `"${c.symbol}"`).join(',');
      const res  = await fetch(`https://data-api.binance.vision/api/v3/ticker/24hr?symbols=[${symbols}]`);
      const data = await res.json();
      const next = {};
      data.forEach(d => { next[d.symbol] = { price: parseFloat(d.lastPrice), change: parseFloat(d.priceChangePercent), high: parseFloat(d.highPrice), low: parseFloat(d.lowPrice), vol: parseFloat(d.quoteVolume) }; });
      setPrev(prices => ({ ...prices }));
      setPrices(next);
    } catch {}
  }, []);

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 10000);
    return () => clearInterval(interval);
  }, [fetchPrices]);

  useEffect(() => {
    CRYPTO_MARKET_LIST.forEach(async (c) => {
      try {
        const res  = await fetch(`https://data-api.binance.vision/api/v3/klines?symbol=${c.symbol}&interval=1h&limit=24`);
        const data = await res.json();
        setSparks(p => ({ ...p, [c.symbol]: data.map(k => parseFloat(k[4])) }));
      } catch {}
    });
  }, []);

  const visibleCoins = onlyFavs ? CRYPTO_MARKET_LIST.filter(c => favs.has(c.symbol)) : CRYPTO_MARKET_LIST;

  return (
    <div className="mk-market-wrap">
      <div className="mk-market-note">
        <FiInfo className="mk-market-note-ic" />
        <span>Estos precios <strong>suben y bajan</strong> (cripto volátil). Para guardar o enviar valor sin sorpresas, operá en <strong>USDT / USDC</strong> — estables, valen ≈ $1.</span>
        <button type="button" className="mk-market-note-btn" onClick={() => onTrade('USDT')}>Ver ofertas en USDT</button>
      </div>
      <div className="mk-market-bar">
        <button className={`mk-watch-toggle ${onlyFavs ? 'active' : ''}`} onClick={() => setOnlyFavs(v => !v)}>
          <FiStar className="mk-watch-ic" /> {onlyFavs ? 'Viendo favoritos' : 'Mi watchlist'}
          {favs.size > 0 && <span className="mk-watch-count fin-num">{favs.size}</span>}
        </button>
      </div>
      {onlyFavs && visibleCoins.length === 0 ? (
        <div className="mk-watch-empty">
          <FiStar className="mk-watch-empty-ic" />
          <p>Tu watchlist está vacía. Tocá la ⭐ en cualquier cripto para seguirla.</p>
          <button className="mk-watch-empty-btn" onClick={() => setOnlyFavs(false)}>Ver todas</button>
        </div>
      ) : (
      <div className="mk-market-grid">
        {visibleCoins.map(c => {
          const d     = prices[c.symbol];
          const p     = prev[c.symbol];
          const up    = d && p ? d.price >= p.price : true;
          const spark = sparks[c.symbol] ?? [];
          const chg   = d?.change ?? 0;
          const chgUp = chg >= 0;
          const isFav = favs.has(c.symbol);

          return (
            <div key={c.symbol} className={`mk-mc ${d && p ? (up ? 'mc-flash-up' : 'mc-flash-down') : ''}`} onClick={() => setSelected(c)}>
              {/* Color accent line */}
              <div className="mc-accent" style={{ background: c.color }} />

              <button
                className={`mc-fav ${isFav ? 'on' : ''}`}
                onClick={(e) => { e.stopPropagation(); toggleFav(c.symbol); }}
                title={isFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                aria-label="Favorito"
              >
                <FiStar />
              </button>

              <div className="mc-body">
                {/* Header: icon + name + change */}
                <div className="mc-header">
                  <div className="mc-icon" style={{ background: c.color + '15', boxShadow: `0 0 12px ${c.color}20` }}>
                    <CoinLogo symbol={c.short} size={24} />
                  </div>
                  <div className="mc-info">
                    <span className="mc-symbol">{c.short}</span>
                    <span className="mc-name">{c.name}</span>
                  </div>
                  <div className={`mc-change fin-num ${chgUp ? 'mc-up' : 'mc-down'}`}>
                    <span className="mc-change-arrow">{chgUp ? '▲' : '▼'}</span>
                    <span>{Math.abs(chg).toFixed(2)}%</span>
                  </div>
                </div>

                {/* Price */}
                <div className={`mc-price fin-num ${chgUp ? 'mc-price-up' : 'mc-price-down'}`}>
                  {d ? `$${d.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: d.price > 1 ? 2 : 6 })}` : '—'}
                </div>

                {/* High / Low / Vol */}
                {d && (
                  <div className="mc-range fin-num">
                    <span className="mc-range-item"><span className="mc-range-label fin-label">H</span> ${d.high?.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    <span className="mc-range-sep">·</span>
                    <span className="mc-range-item"><span className="mc-range-label fin-label">L</span> ${d.low?.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    <span className="mc-range-sep">·</span>
                    <span className="mc-range-item"><span className="mc-range-label fin-label">Vol</span> ${(d.vol / 1e6).toFixed(1)}M</span>
                  </div>
                )}

                {/* Sparkline + Trade */}
                <div className="mc-footer">
                  <Sparkline data={spark} up={chgUp} />
                  <button className="mc-trade-btn" style={{ background: c.color }} onClick={e => { e.stopPropagation(); onTrade(c.short); }}>Operar</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
      {selected && (
        <CryptoChart crypto={selected} onClose={() => setSelected(null)} onTrade={(sym) => { setSelected(null); onTrade(sym); }} />
      )}

      {/* News ticker */}
      <CryptoNewsFeed />
    </div>
  );
}

// ===========================
// CRYPTO NEWS FEED (CoinGecko trending + Binance price moves)
// ===========================
function CryptoNewsFeed() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchNews = async () => {
      try {
        // CoinGecko trending coins
        const res = await fetch('https://api.coingecko.com/api/v3/search/trending');
        const data = await res.json();
        const trending = (data.coins || []).slice(0, 7).map((c, i) => ({
          id: `tr-${i}`,
          title: `${c.item.name} está en tendencia — Rank #${c.item.market_cap_rank || '?'} por capitalización`,
          source: 'CoinGecko',
          time: 'Ahora',
          url: `https://www.coingecko.com/en/coins/${c.item.id}`,
          tags: [c.item.symbol.toUpperCase()],
          logo: c.item.small,
        }));

        // CoinGecko trending NFTs
        const nfts = (data.nfts || []).slice(0, 3).map((n, i) => ({
          id: `nft-${i}`,
          title: `${n.name} NFT trending — Floor: ${n.floor_price_in_native_currency?.toFixed(3) || '?'} ETH`,
          source: 'CoinGecko NFT',
          time: 'Ahora',
          url: `https://www.coingecko.com/en/nft/${n.id}`,
          tags: ['NFT'],
        }));

        // Price movement news from Binance
        try {
          const symbols = CRYPTO_MARKET_LIST.map(c => `"${c.symbol}"`).join(',');
          const pRes = await fetch(`https://data-api.binance.vision/api/v3/ticker/24hr?symbols=[${symbols}]`);
          const pData = await pRes.json();
          const priceNews = pData
            .map(d => {
              const chg = parseFloat(d.priceChangePercent);
              if (Math.abs(chg) < 2) return null;
              const crypto = CRYPTO_MARKET_LIST.find(c => c.symbol === d.symbol);
              if (!crypto) return null;
              const price = parseFloat(d.lastPrice);
              return {
                id: `px-${d.symbol}`,
                pct: Math.abs(chg),
                title: chg > 0
                  ? `${crypto.name} sube ${Math.abs(chg).toFixed(2)}% en 24h — cotiza a $${price.toLocaleString('en-US', { maximumFractionDigits: price > 1 ? 2 : 6 })}`
                  : `${crypto.name} cae ${Math.abs(chg).toFixed(2)}% en 24h — cotiza a $${price.toLocaleString('en-US', { maximumFractionDigits: price > 1 ? 2 : 6 })}`,
                source: 'Binance',
                time: '24h',
                url: `https://www.coingecko.com/en/coins/${crypto.name.toLowerCase()}`,
                tags: [crypto.short],
                logo: crypto.logo,
              };
            })
            .filter(Boolean)
            .sort((a, b) => b.pct - a.pct);

          setNews([...priceNews, ...trending, ...nfts]);
        } catch {
          setNews([...trending, ...nfts]);
        }
      } catch {
        setNews([]);
      }
      setLoading(false);
    };
    fetchNews();
    const t = setInterval(fetchNews, 120000); // 2 min
    return () => clearInterval(t);
  }, []);

  const filtered = filter === 'all' ? news : news.filter(n => {
    const t = n.title.toLowerCase();
    const map = { btc: ['bitcoin', 'btc'], eth: ['ethereum', 'eth'], sol: ['solana', 'sol'], defi: ['defi', 'swap', 'yield', 'staking'] };
    return (map[filter] || []).some(kw => t.includes(kw));
  });

  return (
    <div className="mc-news">
      <div className="mc-news-top">
        <div className="mc-news-header">
          <FiRadio className="mc-news-ic" />
          <span>Noticias Crypto</span>
          <span className="mc-news-live"><span className="mc-news-live-dot" /> Live</span>
        </div>
        <div className="mc-news-filters">
          {[{ k: 'all', l: 'Todas' }, { k: 'btc', l: 'BTC' }, { k: 'eth', l: 'ETH' }, { k: 'sol', l: 'SOL' }, { k: 'defi', l: 'DeFi' }].map(f => (
            <button key={f.k} className={`mc-nf ${filter === f.k ? 'active' : ''}`} onClick={() => setFilter(f.k)}>{f.l}</button>
          ))}
        </div>
      </div>

      <div className="mc-news-list">
        {loading ? (
          <div className="mc-news-empty"><span className="mc-news-spinner" /> Cargando noticias...</div>
        ) : filtered.length === 0 ? (
          <div className="mc-news-empty">Sin noticias para este filtro</div>
        ) : (
          filtered.map(n => (
            <a key={n.id} href={n.url} target="_blank" rel="noopener noreferrer" className="mc-news-row">
              <span className="mc-news-time">{n.time}</span>
              <span className="mc-news-title">{n.title}</span>
              <span className="mc-news-src">{n.source}</span>
              {n.tags.length > 0 && (
                <span className="mc-news-tags">{n.tags.slice(0, 3).map(t => <span key={t} className="mc-news-tag">{t}</span>)}</span>
              )}
            </a>
          ))
        )}
      </div>

      <div className="mc-news-foot">
        <span>Fuente: CoinGecko + Binance · {filtered.length} noticias</span>
      </div>
    </div>
  );
}

// ===========================
// HELPERS
// ===========================
function splitList(str) {
  if (!str) return [];
  return str.split(',').map(s => s.trim()).filter(Boolean);
}

function MoreBadge({ rest }) {
  if (!rest || rest.length === 0) return null;
  return <span className="mk-more-badge" title={rest.join(', ')}>+{rest.length}</span>;
}

function normalizeApiOffer(o) {
  return {
    id:           `api-${o.id}`,
    trader:       o.trader      ?? o.user?.username ?? 'Trader',
    type:         o.type        ?? 'sell',
    currency:     o.currency    ?? 'USDT',
    country:      o.country && o.country !== 'all' ? o.country : (o.user?.country ?? 'all'),
    amount:       parseFloat(o.amount)       || 0,
    minAmount:    parseFloat(o.minAmount)    || 0,
    blockchain:   o.network     ?? o.blockchain ?? 'Bitcoin',
    payment:      o.payment     ?? 'Transferencia bancaria',
    responseTime: parseInt(o.responseTime)   || 5,
    spread:       parseFloat(o.spread)       || 0,
    rating:       parseFloat(o.rating)       || 4.0,
    trades:       parseInt(o.trades)         || 0,
    viewers:      0,
    sellerId:     o.userId,
    avatar:       o.user?.avatar ?? null,
  };
}

function normalizeOffer(o, idx) {
  return {
    id:           o.id            ?? `ext-${idx}`,
    trader:       o.trader        ?? 'Anónimo',
    type:         o.type          ?? 'sell',
    currency:     o.currency      ?? 'USDT',
    country:      o.country && o.country !== 'all' ? o.country : (o.user?.country ?? 'all'),
    amount:       parseFloat(o.amount)       || 0,
    minAmount:    parseFloat(o.minAmount)    || 0,
    blockchain:   o.network       ?? o.blockchain ?? 'Bitcoin',
    payment:      o.paymentMethod ?? o.payment    ?? 'Transferencia bancaria',
    responseTime: parseInt(o.responseTime)   || 5,
    spread:       parseFloat(o.spread)       || 0,
    rating:       parseFloat(o.rating)       || 4.0,
    trades:       parseInt(o.trades)         || 0,
    viewers:      parseInt(o.viewers)        || 0,
  };
}

function getTrustScore(offer) {
  const ratingScore = (parseFloat(offer.rating) / 5) * 40;
  const tradesScore = Math.min(parseInt(offer.trades) / 500, 1) * 35;
  const speedScore  = Math.max(0, (10 - parseInt(offer.responseTime)) / 10) * 25;
  return Math.round(ratingScore + tradesScore + speedScore);
}

function getTrustColor(score) {
  // Vars theme-aware: en claro van oscurecidas (el verde/amarillo brillante es ilegible
  // sobre blanco), en oscuro brillan. Definidas en Marketplace.css.
  if (score >= 80) return 'var(--mk-trust-high)';
  if (score >= 60) return 'var(--mk-trust-mid)';
  return 'var(--mk-trust-low)';
}

// Reputación de trading estilo P2P (finalización, tiempo de liberación, actividad 30d).
// Determinística por trader → estable entre renders (mock hasta tener backend).
function repHash(s) { s = String(s || ''); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; }
function traderRep(offer) {
  const h = repHash(offer.trader);
  const trades = parseInt(offer.trades) || 0;
  const rating = parseFloat(offer.rating) || 0;
  const completion = Math.min(99.9, +(90 + (rating / 5) * 7 + Math.min(trades / 500, 1) * 2 + (h % 10) / 10).toFixed(1));
  const release = Math.max(1, Math.round(2 + (h % 5) + Math.max(0, 8 - (parseInt(offer.responseTime) || 5)) / 3));
  const orders30d = Math.max(3, Math.round(trades * 0.06) + (h % 12));
  return { completion, release, orders30d };
}

function getTraderlevel(trades) {
  if (trades === 0) return 'new';
  if (trades < 50) return 'bronze';
  if (trades < 200) return 'silver';
  return 'gold';
}

function isOfferRecent(offer, secondsThreshold = 5) {
  if (!offer._createdAt) return false;
  const createdTime = new Date(offer._createdAt).getTime();
  const now = Date.now();
  return (now - createdTime) < (secondsThreshold * 1000);
}

const AVATAR_COLORS = ['#0091ea','#00b248','#e53935','#8e24aa','#f4511e','#039be5','#43a047','#d81b60'];
function getAvatarColor(name) {
  return AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];
}

// ===========================
// COUNTRY SELECT
// ===========================
function CountrySelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref             = useRef(null);
  const selected        = getCountry(value);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="mk-country-wrap" ref={ref}>
      <button type="button" className="mk-country-trigger" onClick={() => setOpen(o => !o)}>
        <Flag code={selected.code} className="mk-country-flag-lg" />
        <span className="mk-country-name">{selected.label}</span>
        <span className="mk-country-arrow">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="mk-country-dropdown">
          {COUNTRIES.map(c => (
            <div key={c.code} className={`mk-country-option ${value === c.code ? 'active' : ''}`} onClick={() => { onChange(c.code); setOpen(false); }}>
              <Flag code={c.code} className="mk-country-flag-lg" />
              <span>{c.label}</span>
              {value === c.code && <span className="mk-country-check">✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================
// OFFER MODAL
// ===========================
function OfferModal({ offer, onClose, isVerified }) {
  const navigate            = useNavigate();
  const { user }            = useAuth();
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const spread = parseFloat(offer.spread) || 0;
  const isOwnOffer      = offer.sellerId && user?.id && Number(offer.sellerId) === Number(user.id);
  const isVenderBlocked = offer.type === 'buy' && !isVerified;
  // Las ofertas demo (mockOffers) no tienen un vendedor real: no se puede abrir un trade.
  const isDemoOffer     = !offer.sellerId;

  const handleSend = async () => {
    if (!amount || isOwnOffer || isVenderBlocked || isDemoOffer) return;
    setLoading(true); setError(null);
    try {
      const res   = await apiFetch('/api/trades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(amount), crypto: offer.currency, price: offer.amount,
          spread: offer.spread, blockchain: splitList(offer.blockchain)[0] ?? offer.blockchain,
          payment: splitList(offer.payment)[0] ?? offer.payment, sellerId: offer.sellerId,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Error al crear el trade'); }
      const trade = await res.json();
      onClose();
      navigate(`/trades/${trade.id}`);
    } catch (err) { setError(err.message); setLoading(false); }
  };

  const chains   = splitList(offer.blockchain);
  const payments = splitList(offer.payment);

  return (
    <div className="mk-modal-overlay" onClick={onClose}>
      <div className="mk-modal" onClick={e => e.stopPropagation()}>
        <div className="mk-modal-header">
          <h3>Ofertar a @{offer.trader}</h3>
          <button className="mk-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mk-modal-body">
          {isOwnOffer && <div className="mk-own-offer-warning">⛔ Esta es tu propia oferta — no podés negociar con vos mismo.</div>}
          {isVenderBlocked && !isOwnOffer && (
            <div className="mk-sell-blocked-modal">
              <span>🔒</span>
              <div><strong>Acceso de vendedor requerido</strong><p>Para vender crypto en Bitplify necesitás un código de invitación.</p></div>
            </div>
          )}
          {isDemoOffer && !isOwnOffer && (
            <div className="mk-sell-blocked-modal">
              <span>👀</span>
              <div><strong>Oferta de demostración</strong><p>Esta es una oferta de ejemplo. Operá con traders reales verificados desde el marketplace.</p></div>
            </div>
          )}
          <div className="mk-offer-summary">
            <div className="mk-offer-summary-item"><span>Oferta</span><strong><span className="fin-label">USDT</span> <span className="fin-num">{Number(offer.amount).toLocaleString()}</span></strong></div>
            <div className="mk-offer-summary-item">
              <span>Blockchain</span>
              <strong className="mk-summary-chains">
                {chains.map(c => (
                  <span key={c} className="fin-chip mk-chain-chip"><ChainDot chain={c} />{c}</span>
                ))}
              </strong>
            </div>
            <div className="mk-offer-summary-item"><span>Pago</span><strong>{payments.join(', ')}</strong></div>
            <div className="mk-offer-summary-item">
              <span>Spread</span>
              <strong className="fin-num" style={{ color: spread > 0 ? 'var(--fin-down)' : spread < 0 ? 'var(--fin-up)' : 'var(--mk-text-tertiary)' }}>
                {spread === 0 ? 'TRM Mercado' : spread > 0 ? `+${spread}%` : `${spread}%`}
              </strong>
            </div>
          </div>
          {!isOwnOffer && !isVenderBlocked && !isDemoOffer && (
            <>
              <div className="mk-field">
                <label>Tu oferta (USDT)</label>
                <div className="mk-input-wrap">
                  <span className="mk-input-prefix">USDT</span>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder={`Máx. ${Number(offer.amount).toLocaleString()}`} className="mk-input" min="1" />
                </div>
              </div>
              <div className="mk-field">
                <label>Mensaje (opcional)</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Ej: Puedo pagar ahora mismo..." className="mk-textarea" rows={3} />
              </div>
            </>
          )}
          {error && <div className="mk-error"><FiAlertTriangle className="mk-ic" /> {error}</div>}
          {!isOwnOffer && !isVenderBlocked && !isDemoOffer && <div className="mk-info-box"><FiShield className="mk-info-ic" /><p>Al confirmar se creará el trade y serás redirigido al chat.</p></div>}
          <div className="mk-modal-footer">
            <button className="mk-btn-cancel" onClick={onClose}>{isOwnOffer || isVenderBlocked || isDemoOffer ? 'Cerrar' : 'Cancelar'}</button>
            {!isOwnOffer && !isVenderBlocked && !isDemoOffer && (
              <button className="mk-btn-send" onClick={handleSend} disabled={!amount || loading}>
                {loading ? <><span className="mk-spinner" /> Creando trade...</> : <><FiSend className="mk-ic" /> Confirmar oferta</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================
// SHARE MODAL
// ===========================
function ShareModal({ offer, onClose }) {
  const [copiedUrl, setCopiedUrl]   = useState(false);
  const [copiedMsg, setCopiedMsg]   = useState(false);
  const [shareCount]                = useState(Math.floor(Math.random() * 40) + 3);
  const [editingMsg, setEditingMsg] = useState(false);

  const country   = getCountry(offer.country);
  const spread    = parseFloat(offer.spread) || 0;
  const spreadTxt = spread === 0 ? 'Sin comisión · TRM Mercado' : spread > 0 ? `+${spread}% sobre TRM` : `${spread}% bajo TRM`;
  const shareUrl  = `https://app.bitplify.io/offer/${offer.id}`;
  const defaultMsg = `🚀 Encontré una buena oferta en Bitplify P2P\n\n👤 @${offer.trader} ${country.flag} · ⭐ ${offer.rating} · 🤝 ${offer.trades} trades\n💰 ${offer.type === 'buy' ? 'Comprando' : 'Vendiendo'} ${offer.currency} — USDT ${Number(offer.amount).toLocaleString()} disponibles\n📊 ${spreadTxt}\n💳 ${offer.payment} · ⚡ Responde en ${offer.responseTime} min\n\n👉 ${shareUrl}\n💡 El mercado sos vos`;
  const [message, setMessage] = useState(defaultMsg);

  const copyUrl = () => { navigator.clipboard.writeText(shareUrl); setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2000); };
  const copyMsg = () => { navigator.clipboard.writeText(message); setCopiedMsg(true); setTimeout(() => setCopiedMsg(false), 2000); };
  // Share nativo del sistema (móvil + navegadores modernos): abre el menú con TODAS las apps
  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;
  const nativeShare = async () => {
    try { await navigator.share({ title: 'Oferta en Bitplify P2P', text: message, url: shareUrl }); }
    catch { /* el usuario canceló el menú — no hacemos nada */ }
  };

  return (
    <div className="mk-modal-overlay" onClick={onClose}>
      <div className="mk-modal mk-share-modal" onClick={e => e.stopPropagation()}>
        <div className="mk-modal-header">
          <h3>↗ Compartir oferta</h3>
          <button className="mk-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mk-modal-body mk-share-body">
          <div className="mk-share-preview-card">
            <div className="mk-share-preview-header">
              <div className="mk-share-preview-logo">₿ Bitplify P2P</div>
              <span className={`fin-chip mk-share-preview-badge mk-type-badge ${offer.type}`}>{offer.type === 'buy' ? 'Compra' : 'Venta'}</span>
            </div>
            <div className="mk-share-preview-trader">
              <div className="mk-share-preview-avatar" style={{ background: getAvatarColor(offer.trader) }}>
                {offer.avatar ? <img src={offer.avatar} alt={offer.trader} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : offer.trader.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="mk-share-preview-name">@{offer.trader} <Flag code={country.code} /></div>
                <div className="mk-share-preview-meta"><FaStar className="mk-meta-star" /> <span className="fin-num">{offer.rating}</span> · <span className="fin-num">{offer.trades}</span> trades</div>
              </div>
              <div className="mk-share-preview-amount"><span className="fin-label">USDT</span><strong className="fin-num">{Number(offer.amount).toLocaleString()}</strong></div>
            </div>
            <div className="mk-share-preview-details">
              <span className="fin-chip">{offer.payment}</span>
              <span><span className="fin-num">{offer.responseTime}</span> min</span>
              <span className="fin-num" style={{ color: spread <= 0 ? 'var(--fin-up)' : 'var(--fin-down)' }}>{spreadTxt}</span>
            </div>
            <div className="mk-share-preview-footer">
              <span className="mk-share-preview-url">app.bitplify.io</span>
              <span className="mk-share-preview-fomo"><span className="fin-num">{shareCount}</span> personas compartieron esto</span>
            </div>
          </div>
          {canNativeShare && (
            <button className="mk-share-native" onClick={nativeShare}>
              <FiShare2 style={{ marginRight: 8 }} /> Compartir en cualquier app
            </button>
          )}
          <div className="mk-share-msg-section">
            <div className="mk-share-msg-header">
              <span className="mk-share-label">Mensaje para compartir</span>
              <button className="mk-share-edit-btn" onClick={() => setEditingMsg(p => !p)}>{editingMsg ? 'Listo' : 'Editar'}</button>
            </div>
            {editingMsg
              ? <textarea className="mk-share-msg-textarea" value={message} onChange={e => setMessage(e.target.value)} rows={6} />
              : <div className="mk-share-msg-preview">{message}</div>
            }
            <button className={`mk-share-copy-msg ${copiedMsg ? 'copied' : ''}`} onClick={copyMsg}>
              {copiedMsg ? 'Mensaje copiado' : 'Copiar mensaje completo'}
            </button>
          </div>
          <div className="mk-share-link-row">
            <div className="mk-share-url">
              <span>{shareUrl}</span>
              <button className={`mk-copy-btn ${copiedUrl ? 'copied' : ''}`} onClick={copyUrl}>{copiedUrl ? 'Copiado' : 'Copiar link'}</button>
            </div>
          </div>
          <div className="mk-share-social-section">
            <span className="mk-share-label">Compartir en:</span>
            <div className="mk-share-btns-grid">
              <a href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noopener noreferrer" className="mk-share-social-btn whatsapp"><span>WhatsApp</span></a>
              <a href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(message.split('\n').slice(0,3).join('\n'))}`} target="_blank" rel="noopener noreferrer" className="mk-share-social-btn telegram"><span>Telegram</span></a>
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(message.slice(0, 240))}`} target="_blank" rel="noopener noreferrer" className="mk-share-social-btn twitter"><span>Twitter/X</span></a>
              <button className="mk-share-social-btn instagram" onClick={() => { navigator.clipboard.writeText(message); alert('Mensaje copiado — pégalo en Instagram'); }}><span>Instagram</span></button>
            </div>
          </div>
          <div className="mk-share-fomo"><strong><span className="fin-num">{shareCount}</span> personas</strong> compartieron esta oferta · Sé el primero en tu grupo</div>
        </div>
      </div>
    </div>
  );
}

// ===========================
// TRADER CELL
// ===========================
const MK_TIERS = [
  { min: 500, frame: 'legend',  label: 'Leyenda',  color: '#d946ef' },
  { min: 200, frame: 'magenta', label: 'Veterano', color: '#c084fc' },
  { min: 100, frame: 'gold',    label: 'Experto',  color: '#ffd700' },
  { min: 50,  frame: 'cyan',    label: 'Avanzado', color: '#22d3ee' },
  { min: 10,  frame: 'neon',    label: 'Activo',   color: '#00d412' },
  { min: 0,   frame: 'basic',   label: 'Nuevo',    color: '#8a9bb0' },
];
const mkTier = (trades) => MK_TIERS.find((t) => (trades || 0) >= t.min);

function TraderCell({ offer, onNavigate }) {
  const country = getCountry(offer.country);
  const tier = mkTier(offer.trades);
  const pres = traderPresence(offer);
  const [favs, toggleFav] = useFavTraders();
  const isFav = favs.has(offer.trader);
  const [pop, setPop] = useState(null);
  const go = (e) => { e.stopPropagation(); onNavigate(offer.trader); };

  const openPop = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const below = r.bottom + 240 < window.innerHeight;
    setPop({
      left: Math.max(8, Math.min(r.left, window.innerWidth - 252)),
      top: below ? r.bottom + 8 : r.top - 8,
      below,
    });
  };

  const trust = getTrustScore(offer);
  const badges = [];
  if (offer.responseTime <= 3) badges.push({ t: '⚡ Rápido', c: '#22d3ee' });
  if (offer.trades >= 200) badges.push({ t: '🔥 Top trader', c: '#ff6b35' });
  if (offer.trades === 0) badges.push({ t: '🆕 Nuevo', c: '#8a9bb0' });

  return (
    <div
      className="mk-trader-cell mk-trader-cell-link"
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') go(e); }}
      onMouseEnter={openPop}
      onMouseLeave={() => setPop(null)}
      title={`Ver perfil de @${offer.trader}`}
    >
      <span className="mk-avatar-wrap">
        <Avatar u={{ username: offer.trader, name: offer.trader, avatar: offer.avatar }} frame={tier.frame} size={42} />
        <span className={`mk-presence ${pres.online ? 'on' : 'off'}`} title={pres.online ? 'En línea ahora' : `Visto ${pres.lastSeen}`} aria-hidden="true" />
      </span>
      <div className="mk-trader-info">
        <div className="mk-trader-name-row">
          <span className="mk-trader-name mk-trader-name-link">@{offer.trader}</span>
          <span className="mk-country-badge" title={country.label}><Flag code={country.code} /></span>
          <span className="mk-tier-chip" style={{ color: tier.color, borderColor: `${tier.color}55` }}>{tier.label}</span>
          <button type="button" className={`mk-fav-trader ${isFav ? 'on' : ''}`}
            title={isFav ? 'Quitar de favoritos' : 'Seguir comerciante'} aria-label="Favorito"
            onClick={(e) => { e.stopPropagation(); toggleFav(offer.trader); }}>
            <FiStar />
          </button>
        </div>
        <div className="mk-trader-meta">
          <span className={`mk-status ${pres.online ? 'on' : 'off'}`} title={pres.online ? 'En línea ahora' : `Última conexión ${pres.lastSeen}`}>
            <span className="mk-status-dot" />{pres.online ? 'En línea' : pres.lastSeen}
          </span>
          <span><span className="fin-num">{offer.trades}</span> trades</span>
          <span><FaStar className="mk-meta-star" /> <span className="fin-num">{offer.rating}</span></span>
          {offer.viewers > 0 && <span className="mk-viewers" title={`${offer.viewers} viendo ahora`}>👀 <span className="fin-num">{offer.viewers}</span></span>}
          <span className="mk-trader-viewprofile">Ver perfil ↗</span>
        </div>
      </div>

      {pop && (
        <div className={`mk-trader-pop ${pop.below ? 'below' : 'above'}`} style={{ left: pop.left, top: pop.top }}>
          <div className="mk-pop-head">
            <Avatar u={{ username: offer.trader, name: offer.trader, avatar: offer.avatar }} frame={tier.frame} size={46} />
            <div className="mk-pop-id">
              <div className="mk-pop-name">@{offer.trader} <span className="mk-country-badge"><Flag code={country.code} /></span></div>
              <div className="mk-pop-tier" style={{ color: tier.color }}>⬡ {tier.label}</div>
            </div>
          </div>
          {badges.length > 0 && (
            <div className="mk-pop-badges">
              {badges.map((b, i) => <span key={i} className="mk-pop-badge" style={{ color: b.c, borderColor: `${b.c}55` }}>{b.t}</span>)}
            </div>
          )}
          <div className="mk-pop-stats">
            <div><strong className="fin-num">{offer.trades}</strong><span>Trades</span></div>
            <div><strong className="fin-num">{offer.rating}</strong><span>Rating</span></div>
            <div><strong className="fin-num">{offer.responseTime}m</strong><span>Resp.</span></div>
            <div><strong className="fin-num" style={{ color: getTrustColor(trust) }}>{trust}</strong><span>Confianza</span></div>
          </div>
          {offer.viewers > 0 && <div className="mk-pop-viewers">👀 {offer.viewers} viendo ahora</div>}
          <div className="mk-pop-cta">Click para ver el perfil completo →</div>
        </div>
      )}
    </div>
  );
}

// ===========================
// OFFER ROW — desktop
// ===========================
// Estimación pagas→recibes según el spread de la oferta (USDT ≈ USD)
function giveGet(offer, amount) {
  const entered = parseFloat(amount) > 0 ? parseFloat(amount) : null;
  const pay     = entered ?? 100;              // referencia $100 si no hay monto escrito
  const spread  = parseFloat(offer.spread) || 0;
  const price   = 1 + spread / 100;            // USD por unidad (comprador paga el premium)
  const receive = pay / (price || 1);
  return { pay, receive, price, isDefault: entered === null };
}

// Categoría + ícono por método de pago
// kind = categoría (ícono); risk = 'high' si el pago es reversible (contracargo) → riesgo para el vendedor
function paymentMeta(name) {
  const n = (name || '').toLowerCase();
  if (/paypal/.test(n))                                                                                        return { kind: 'send',   color: '#0070ba', risk: 'high' };
  if (/venmo|cash ?app/.test(n))                                                                               return { kind: 'send',   color: '#7b3fe4', risk: 'high' };
  if (/zelle/.test(n))                                                                                         return { kind: 'send',   color: '#7b3fe4', risk: 'low'  };
  if (/binance|reserve|crypto|usdt/.test(n))                                                                   return { kind: 'crypto', color: '#f0b90b', risk: 'low'  };
  if (/m[oó]vil|nequi|sinpe|yape|plin|daviplata|zinli|wink|bizum|uala|lemon|naranja|picpay|pago m/.test(n))   return { kind: 'mobile', color: '#16a34a', risk: 'low'  };
  if (/mercado pago/.test(n))                                                                                  return { kind: 'send',   color: '#2f80ed', risk: 'high' };
  if (/pix|spei|codi|khipu|oxxo|abitab|oca|redpagos/.test(n))                                                  return { kind: 'send',   color: '#2f80ed', risk: 'low'  };
  if (/banc|transfer|bbva|banamex|santander|bci|ita|nubank|brubank|banco|mercantil|estado/.test(n))           return { kind: 'bank',   color: '#6b8aaa', risk: 'low'  };
  return { kind: 'card', color: 'var(--mk-text-tertiary)', risk: 'high' };
}

// Ícono de línea por método de pago (reemplaza el pixel art): marca real donde existe, si no por categoría
function PayIcon({ name, color }) {
  const n = (name || '').toLowerCase();
  const { kind } = paymentMeta(name);
  if (/paypal/.test(n))  return <FaPaypal className="mk-pay-ic" style={{ color: '#0070ba' }} />;
  if (kind === 'crypto') return <CoinLogo symbol="USDT" size={14} className="mk-pay-ic" />;
  const Icon = kind === 'bank' ? FaUniversity
             : kind === 'mobile' ? FiSmartphone
             : kind === 'send' ? FiSend
             : FiCreditCard;
  return <Icon className="mk-pay-ic" style={{ color }} />;
}

function PaymentChip({ name }) {
  const { color, risk } = paymentMeta(name);
  return (
    <span className="fin-chip mk-payment mk-payment-bar" title={name} style={{ '--pay-color': color }}>
      <PayIcon name={name} color={color} />
      <span className="mk-payment-name">{name}</span>
      {risk === 'high' && (
        <FiAlertTriangle className="mk-pay-risk" title="Método reversible: el comprador puede revertir el pago (contracargo). Mayor riesgo para el vendedor — usalo solo con contrapartes de confianza." />
      )}
    </span>
  );
}

// Banner "anuncio destacado" — el de mejor reputación del set filtrado (estilo Binance)
function PromotedBanner({ offer, onBuy, onOffer, onNavigate, isVerified, amount, fiatRates, parRates }) {
  if (!offer) return null;
  const gg          = giveGet(offer, amount);
  const spread      = parseFloat(offer.spread) || 0;
  const spreadColor = spread > 0 ? 'var(--fin-down)' : spread < 0 ? 'var(--fin-up)' : 'var(--mk-text-tertiary)';
  const loc         = localPrice(gg.price, offer.country, fiatRates, parRates);
  const payments    = splitList(offer.payment);
  const isVenderBlocked = offer.type === 'buy' && !isVerified;
  return (
    <div className="mk-promoted">
      <div className="mk-promoted-label">★ Anuncio destacado</div>
      <div className="mk-promoted-body">
        <div className="mk-promoted-trader"><TraderCell offer={offer} onNavigate={onNavigate} /></div>
        <div className="mk-promoted-price">
          <span className="mk-price-big fin-num">${gg.price.toFixed(3)} <span className="mk-price-per">/USDT</span></span>
          <span className="mk-price-tag2 fin-num" style={{ color: spreadColor }}>{spread === 0 ? 'TRM · sin recargo' : spread > 0 ? `+${spread}% premium` : `${spread}% descuento`}</span>
          {loc && <span className="mk-price-local2 fin-num">≈ {loc.str} <span className={`mk-price-local-ref ${loc.parallel ? 'par' : ''}`}>{loc.parallel ? 'paralelo' : 'ref.'}</span></span>}
        </div>
        <div className="mk-promoted-avail">
          <span><span className="fin-num">{Number(offer.amount).toLocaleString()}</span> USDT disponibles</span>
          <div className="mk-promoted-pay"><PaymentChip name={payments[0]} />{payments.length > 1 && <MoreBadge rest={payments.slice(1)} />}</div>
        </div>
        <div className="mk-promoted-action">
          {isVenderBlocked
            ? <button className="mk-btn-trade locked" onClick={() => onOffer(offer)} title="Solo vendedores verificados">Por invitación</button>
            : <button className={`mk-btn-trade ${offer.type === 'buy' ? 'sell' : 'buy'}`} onClick={() => onBuy(offer)}>{offer.type === 'buy' ? 'Vender USDT' : 'Comprar USDT'}</button>}
        </div>
      </div>
    </div>
  );
}

function OfferRow({ offer, onOffer, onBuy, onShare, onNavigate, isVerified, amount, best, fiatRates, parRates }) {
  const trust       = getTrustScore(offer);
  const trustColor  = getTrustColor(trust);
  const rep         = traderRep(offer);
  const spread      = parseFloat(offer.spread) || 0;
  const spreadColor = spread > 0 ? 'var(--fin-down)' : spread < 0 ? 'var(--fin-up)' : 'var(--mk-text-tertiary)';
  const chains      = splitList(offer.blockchain);
  const payments    = splitList(offer.payment);
  const isVenderBlocked = offer.type === 'buy' && !isVerified;
  const gg       = giveGet(offer, amount);
  const loc      = localPrice(gg.price, offer.country, fiatRates, parRates);
  const featured = (offer.featuredUntil && new Date(offer.featuredUntil).getTime() > Date.now())
    || (parseFloat(offer.rating) >= 4.8 && parseInt(offer.trades) >= 100);

  const completionColor = rep.completion >= 95 ? 'var(--fin-up)' : 'var(--mk-text-secondary)';
  const limitMax = offer.maxAmount > 0 ? offer.maxAmount : offer.amount;

  return (
    <tr className="mk-row">
      {/* 1 · ANUNCIANTE — avatar + nombre + badges + stats agrupadas (estilo Binance) */}
      <td className="mk-td-advertiser">
        <TraderCell offer={offer} onNavigate={onNavigate} />
        <div className="mk-adv-badges">
          <span className={`mk-type-badge ${offer.type}`}>{offer.type === 'buy' ? 'Compra' : 'Venta'}</span>
          {best && <span className="mk-badge-best">Mejor precio</span>}
          {featured && <span className="mk-badge-featured">★</span>}
          {isOfferRecent(offer) && <span className="mk-badge-new">Nuevo</span>}
        </div>
        <div className="mk-adv-stats mk-trust-link" onClick={() => onNavigate(offer.trader)} title={`Ver reputación de @${offer.trader}`}>
          <span><span className="fin-num">{rep.orders30d}</span> órdenes</span>
          <span className="mk-adv-sep">·</span>
          <span style={{ color: completionColor }}><FiCheck className="mk-rep-ic" /> <span className="fin-num">{rep.completion}%</span></span>
          <span className="mk-adv-sep">·</span>
          <span title="Tiempo de respuesta">⏱ <span className="fin-num">{offer.responseTime}</span> min</span>
          <span className="mk-adv-sep">·</span>
          <span style={{ color: trustColor }} title="Puntaje de confianza">Conf. <span className="fin-num">{trust}</span></span>
        </div>
      </td>

      {/* 2 · PRECIO — protagonista */}
      <td className="mk-td-price">
        <div className="mk-price-big fin-num">${gg.price.toFixed(3)} <span className="mk-price-per">/USDT</span></div>
        <div className="mk-price-tag2 fin-num" style={{ color: spreadColor }}>
          {spread === 0 ? 'TRM · sin recargo' : spread > 0 ? `+${spread}% premium` : `${spread}% descuento`}
        </div>
        {loc && (
          <div className="mk-price-local2 fin-num"
            title={loc.parallel
              ? 'Tipo de cambio paralelo (el real de mercado, no el oficial). Referencia — el precio final lo acuerdan las partes.'
              : 'Conversión de referencia al tipo de cambio oficial; en P2P el precio real lo acuerdan las partes.'}>
            ≈ {loc.str} <span className={`mk-price-local-ref ${loc.parallel ? 'par' : ''}`}>{loc.parallel ? 'paralelo' : 'ref.'}</span>
          </div>
        )}
      </td>

      {/* 3 · DISPONIBLE / LÍMITE */}
      <td className="mk-td-avail">
        <div className="mk-avail-amt"><span className="fin-num">{Number(offer.amount).toLocaleString()}</span> <span className="fin-label">USDT</span></div>
        <div className="mk-avail-limit">Límite {offer.minAmount > 0
          ? <span className="fin-num">{Number(offer.minAmount).toLocaleString()} – {Number(limitMax).toLocaleString()}</span>
          : <>hasta <span className="fin-num">{Number(limitMax).toLocaleString()}</span></>}</div>
        {gg && <div className="mk-avail-get">Recibís ≈ <span className="fin-num">{gg.receive.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>{gg.isDefault && <span className="mk-gg-ref"> /$100</span>}</div>}
      </td>

      {/* 4 · PAGO (+ red) */}
      <td className="mk-td-pay">
        <div className="mk-payment-cell"><PaymentChip name={payments[0]} /><MoreBadge rest={payments.slice(1)} /></div>
        <div className="mk-pay-chain"><ChainGlyph chain={chains[0]} px={1} /> <span>{chains[0]}</span>{chains.length > 1 && <MoreBadge rest={chains.slice(1)} />}</div>
      </td>

      {/* 5 · TRADE — un botón protagonista + secundarios */}
      <td className="mk-td-trade">
        {isVenderBlocked ? (
          <button className="mk-btn-trade locked" onClick={() => onOffer(offer)} title="Solo vendedores verificados">Por invitación</button>
        ) : (
          <button className={`mk-btn-trade ${offer.type === 'buy' ? 'sell' : 'buy'}`} onClick={() => onBuy(offer)}>{offer.type === 'buy' ? 'Vender USDT' : 'Comprar USDT'}</button>
        )}
        <div className="mk-trade-secondary">
          <button className="mk-btn-mini" onClick={() => onOffer(offer)} title="Proponé tu propio precio o monto (negociar)"><FiDollarSign style={{ marginRight: 3, verticalAlign: -1 }} />Ofertar</button>
          <button className="mk-btn-mini" onClick={() => onShare(offer)} title="Compartir esta oferta"><FiShare2 style={{ marginRight: 3, verticalAlign: -1 }} />Compartir</button>
        </div>
      </td>
    </tr>
  );
}

// ===========================
// OFFER CARD — mobile
// ===========================
function OfferCard({ offer, onOffer, onBuy, onShare, onNavigate, isVerified, amount, best, fiatRates, parRates }) {
  const trust       = getTrustScore(offer);
  const trustColor  = getTrustColor(trust);
  const rep         = traderRep(offer);
  const spread      = parseFloat(offer.spread) || 0;
  const spreadColor = spread > 0 ? 'var(--fin-down)' : spread < 0 ? 'var(--fin-up)' : 'var(--mk-text-tertiary)';
  const spreadLabel = spread === 0 ? 'TRM Mercado' : spread > 0 ? `+${spread}% premium` : `${spread}% descuento`;
  const country     = getCountry(offer.country);
  const chains      = splitList(offer.blockchain);
  const payments    = splitList(offer.payment);
  const isVenderBlocked = offer.type === 'buy' && !isVerified;
  const gg       = giveGet(offer, amount);
  const loc      = localPrice(gg.price, offer.country, fiatRates, parRates);
  const featured = (offer.featuredUntil && new Date(offer.featuredUntil).getTime() > Date.now())
    || (parseFloat(offer.rating) >= 4.8 && parseInt(offer.trades) >= 100);

  return (
    <div className="mk-card">
      <div className="mk-card-header">
        <TraderCell offer={offer} onNavigate={onNavigate} />
        <div className="mk-card-badges">
          <span className={`mk-type-badge ${offer.type}`}>{offer.type === 'buy' ? 'Compra' : 'Venta'}</span>
          {best && <span className="mk-badge-best">Mejor precio</span>}
          {featured && <span className="mk-badge-featured">★ Destacada</span>}
          {isOfferRecent(offer) && <span className="mk-badge-new">Nuevo</span>}
          {offer.viewers > 0 && <span className="mk-viewers"><span className="fin-num">{offer.viewers}</span> vistas</span>}
        </div>
      </div>
      <div className="mk-card-body">
        <div className="mk-card-row"><span>País</span><strong><Flag code={country.code} /> {country.label}</strong></div>
        <div className="mk-card-row"><span>Disponible</span><strong><span className="fin-label">USDT</span> <span className="fin-num">{Number(offer.amount).toLocaleString()}</span></strong></div>
        <div className="mk-card-row"><span>Blockchain</span><strong><span className="fin-chip mk-chain-chip"><ChainDot chain={chains[0]} />{chains[0]}</span>{chains.length > 1 && <MoreBadge rest={chains.slice(1)} />}</strong></div>
        <div className="mk-card-row"><span>Pago</span><strong title={payments.join(', ')}><PaymentChip name={payments[0]} />{payments.length > 1 && <MoreBadge rest={payments.slice(1)} />}</strong></div>
        <div className="mk-card-row"><span>Precio</span><strong className="fin-num" style={{ color: spreadColor }}>{spreadLabel} <span className="mk-effprice">· ${gg.price.toFixed(3)}/USDT</span></strong></div>
        {loc && <div className="mk-card-row"><span>Precio local</span><strong className="fin-num">≈ {loc.str}/USDT <span className={`mk-price-local-ref ${loc.parallel ? 'par' : ''}`}>{loc.parallel ? 'paralelo' : 'ref.'}</span></strong></div>}
        <div className="mk-card-row"><span>Respuesta</span><strong><span className="fin-num">{offer.responseTime}</span> min</strong></div>
        <div className="mk-card-row"><span>Finalización</span><strong className="fin-num" style={{ color: rep.completion >= 95 ? 'var(--fin-up)' : 'var(--mk-text-primary)' }}><FiCheck className="mk-rep-ic" /> {rep.completion}%</strong></div>
        <div className="mk-card-row"><span>Liberación</span><strong>~<span className="fin-num">{rep.release}</span> min · <span className="fin-num">{rep.orders30d}</span> órdenes/30d</strong></div>
        <div className="mk-card-row"><span>Confianza</span><strong className="fin-num" style={{ color: trustColor }}>{trust}/100</strong></div>
      </div>
      {gg && (
        <div className="mk-getget-band">
          <div className="mk-gg-col"><span className="fin-label">{gg.isDefault ? 'Pagas (ref.)' : 'Pagas'}</span><strong className="fin-num">${gg.pay.toLocaleString()}</strong></div>
          <span className="mk-gg-arrow">→</span>
          <div className="mk-gg-col"><span className="fin-label">Recibes</span><strong className="fin-num">{gg.receive.toLocaleString('en-US', { maximumFractionDigits: 2 })} USDT</strong></div>
        </div>
      )}
      <div className="mk-card-actions">
        {isVenderBlocked ? (
          <button className="mk-btn-locked" onClick={() => onOffer(offer)}>Requiere invitación</button>
        ) : (
          <button className={`mk-btn-action ${offer.type === 'buy' ? 'sell' : 'buy'}`} onClick={() => onBuy(offer)}>
            <FiShoppingCart style={{ marginRight: 6, verticalAlign: -2 }} />{offer.type === 'buy' ? 'Vender USDT' : 'Comprar USDT'}
          </button>
        )}
        <button className="mk-btn-offer" onClick={() => onOffer(offer)} title="Proponé tu propio precio o monto al anunciante (negociar)">
          <FiDollarSign style={{ marginRight: 6, verticalAlign: -2 }} />Ofertar mi precio
        </button>
        <button className="mk-btn-share" onClick={() => onShare(offer)} title="Compartir esta oferta por WhatsApp, Telegram, etc.">
          <FiShare2 style={{ marginRight: 6, verticalAlign: -2 }} />Compartir
        </button>
      </div>
      <div className="mk-actions-help">
        <FiHelpCircle className="mk-actions-help-ic" />
        <span><strong>Comprar/Vender</strong> = operás al precio publicado · <strong>Ofertar</strong> = proponés tu precio · <strong>Compartir</strong> = enviás la oferta</span>
      </div>
    </div>
  );
}

// ===========================
// MARKETPLACE PRINCIPAL
// ===========================
export default function Marketplace({ externalOffers = [] }) {
  const navigate      = useNavigate();
  const routerLoc     = useLocation();
  const locationState = window.history.state?.usr;
  // El tab activo lo manda la URL: / = P2P, /precios = live price, /muro = comunidad
  const pathTab = TAB_BY_PATH[routerLoc.pathname] || 'offers';
  // Cambiar de tab = navegar (la URL refleja dónde está el usuario, es compartible)
  const goTab = (t) => navigate(PATH_BY_TAB[t] || '/');

  const [successToast, setSuccessToast] = useState(
    locationState?.offerCreated
      ? `🎉 ¡Oferta de ${locationState.offerType === 'buy' ? 'Compra' : 'Venta'} por USDT ${Number(locationState.offerAmount).toLocaleString()} publicada!`
      : ''
  );

  useEffect(() => {
    if (successToast) { const t = setTimeout(() => setSuccessToast(''), 4000); return () => clearTimeout(t); }
  }, [successToast]);

  const { user, updateUser } = useAuth();
  const { requireAuth, openAuth } = useAuthGate();
  const isVerified = user?.verified === true;
  const fiatRates  = useFiatRates();
  const parRates   = useParallelRates();

  const [mainTab, setMainTab]       = useState(pathTab);
  // Mantené el tab en sync con la URL (back/forward del navegador, links directos)
  useEffect(() => { setMainTab(pathTab); }, [pathTab]);
  const [search, setSearch]         = useState('');
  const [amount, setAmount]         = useState('');
  const [typeFilter, setType]       = useState('all');
  const [chainFilter, setChain]     = useState('Todas');
  const [payFilter, setPay]         = useState('Todos');
  const [cryptoFilter, setCrypto]   = useState('Todas');
  const [countryFilter, setCountry] = useState('all');
  const [sortBy, setSortBy]         = useState('default');
  const [sortDir, setSortDir]       = useState('asc'); // 'asc' or 'desc'
  const [page, setPage]             = useState(1);
  const loadMoreRef                 = useRef(null);
  const [offerModal, setOfferModal] = useState(null);
  const [shareModal, setShareModal] = useState(null);
  const [apiOffers, setApiOffers]   = useState([]);

  // Chips rápidos (estilo NoOnes/Bybit) — filtros de un toque
  const [chips, setChips] = useState({ stable: false, trm: false, fast: false, verified: false, online: false, fav: false });
  const toggleChip = (k) => { setChips(c => ({ ...c, [k]: !c[k] })); setPage(1); };
  const [favTraders] = useFavTraders();

  // Banner educativo (stablecoin + cómo funciona) — recordamos si lo cerró
  const [eduOpen, setEduOpen] = useState(() => {
    try { return localStorage.getItem('mk_eduDismissed') !== '1'; } catch { return true; }
  });
  const dismissEdu = () => { setEduOpen(false); try { localStorage.setItem('mk_eduDismissed', '1'); } catch {} };
  const [eduCenter, setEduCenter] = useState(null); // null | 'como' | 'glos' | 'estafa'

  // Advanced filters state with localStorage persistence
  const [advFilters, setAdvFilters] = useState(() => {
    try {
      const saved = localStorage.getItem('mk_advFilters');
      return saved ? JSON.parse(saved) : {
        minRating: 0,
        minTrades: 0,
        maxResponseTime: 120,
        verifiedOnly: false,
        traderLevel: 'all',
      };
    } catch {
      return {
        minRating: 0,
        minTrades: 0,
        maxResponseTime: 120,
        verifiedOnly: false,
        traderLevel: 'all',
      };
    }
  });

  useEffect(() => {
    const sync = async () => {
      try {
        if (!localStorage.getItem('token')) return;
        const res = await apiFetch('/api/me');
        if (res.ok) {
          const u = await res.json();
          if (u.verified !== user?.verified) updateUser({ verified: u.verified });
        }
      } catch {}
    };
    if (user?.id) sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, updateUser]);

  // Persist advanced filters to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('mk_advFilters', JSON.stringify(advFilters));
    } catch {}
  }, [advFilters]);

  // Oculta el asistente Arkana en tabs densos (Mercado/Portafolio): sus grillas se llenan y la mascota las tapaba
  useEffect(() => {
    document.body.classList.toggle('mk-hide-arkana', mainTab === 'market' || mainTab === 'portfolio');
    return () => document.body.classList.remove('mk-hide-arkana');
  }, [mainTab]);

  // Deep-link desde la landing (bitplify.io): pre-aplica filtros con ?want&have&amount&method&side
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (![...p.keys()].length) return;

    const want = p.get('want');
    if (want && CRYPTOS.includes(want)) setCrypto(want);

    const have = p.get('have');
    const country = have ? FIAT_TO_COUNTRY[have] : null;
    if (country) setCountry(country);

    const method = p.get('method');
    const pay = method ? METHOD_TO_PAY[method] : null;
    if (pay && (PAYMENTS_BY_COUNTRY[country || 'all'] || []).includes(pay)) setPay(pay);

    const amt = p.get('amount');
    if (amt && !Number.isNaN(parseFloat(amt))) setAmount(amt);

    // side de la landing = intención del usuario; el offer.type es merchant-centric → se invierte
    const side = p.get('side');
    if (side === 'buy') setType('sell');        // quiere comprar → ofertas de venta
    else if (side === 'sell') setType('buy');   // quiere vender → ofertas de compra

    setMainTab('offers');
    setPage(1);

    // Limpia el query de la URL una vez aplicados los filtros: deja app.bitplify.io/ prolijo
    // y evita que se re-apliquen al recargar. (utm ya se capturó en la carga inicial.)
    window.history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time subscription handler for Socket.io offer updates
  const handleOfferUpdate = useCallback((update, action) => {
    if (action === 'initial') {
      // Initial load of all offers
      setApiOffers(update.map(normalizeApiOffer));
    } else if (action === 'create') {
      // New offer created - add to beginning of list
      setApiOffers(prev => [normalizeApiOffer(update[0]), ...prev]);
    } else if (action === 'update') {
      // Existing offer updated
      setApiOffers(prev =>
        prev.map(o => o.id === update[0].id ? normalizeApiOffer(update[0]) : o)
      );
    } else if (action === 'delete') {
      // Offer deactivated - remove from list
      setApiOffers(prev => prev.filter(o => o.id !== update.id));
    }
  }, []);

  // Use Socket.io subscription instead of polling
  useOfferSubscription(handleOfferUpdate);

  const handleBuy = (offer) => navigate('/trade/confirm', { state: { offer } });

  // Gate por acción: invitado -> AuthModal; al entrar, retoma la acción
  const handleBuyGated   = (offer) => requireAuth(() => handleBuy(offer));
  const handleOfferGated = (offer) => requireAuth(() => setOfferModal(offer));

  // "Ver ofertas de $X" desde Precios/Muro → va al P2P (/) con el filtro, vía la URL
  const handleTradeFromMarket = (symbol) => navigate('/', { state: { mkCrypto: symbol } });

  // Aplica el filtro de cripto que llega por navegación (sobrevive el cambio de ruta)
  useEffect(() => {
    if (routerLoc.state?.mkCrypto) { setCrypto(routerLoc.state.mkCrypto); setPage(1); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLoc.key]);

  const handleNavigateToTrader = (traderName) => navigate(`/profile/${traderName}`);
  const availablePayments  = PAYMENTS_BY_COUNTRY[countryFilter] ?? PAYMENTS_BY_COUNTRY['all'];
  const normalizedExternal = useMemo(() => externalOffers.map((o, i) => normalizeOffer(o, i)), [externalOffers]);
  const allOffers          = useMemo(() => {
    const real = [...apiOffers, ...normalizedExternal];
    // Mock solo como demo cuando aún NO hay ofertas reales (nunca mezclar falsas con reales en producción)
    return real.length > 0 ? real : MOCK_OFFERS;
  }, [apiOffers, normalizedExternal]);

  const filtered = useMemo(() => {
    let result = allOffers.filter(o => {
      // Basic filters
      const matchSearch  = o.trader.toLowerCase().includes(search.toLowerCase());
      const matchType    = typeFilter === 'all' || o.type === typeFilter;
      const matchChain   = chainFilter === 'Todas' || o.blockchain.includes(chainFilter);
      const matchPay     = payFilter === 'Todos' || o.payment.includes(payFilter);
      const matchCrypto  = cryptoFilter === 'Todas' || o.currency === cryptoFilter;
      const matchCountry = countryFilter === 'all' || o.country === countryFilter;

      // Advanced filters
      const matchRating = parseFloat(o.rating) >= advFilters.minRating;
      const matchTrades = parseInt(o.trades) >= advFilters.minTrades;
      const matchResponseTime = parseInt(o.responseTime) <= advFilters.maxResponseTime;

      const traderLevel = getTraderlevel(parseInt(o.trades));
      const matchLevel = advFilters.traderLevel === 'all' || traderLevel === advFilters.traderLevel;

      const matchVerified = !advFilters.verifiedOnly || o.user?.verified === true;

      const amt = parseFloat(amount);
      const matchAmount = !amt || amt <= 0 || (amt >= (parseFloat(o.minAmount) || 0) && amt <= (parseFloat(o.amount) || Infinity));

      // Chips rápidos
      const matchStable  = !chips.stable   || isStable(o.currency);
      const matchTrm     = !chips.trm      || (parseFloat(o.spread) || 0) === 0;
      const matchFast    = !chips.fast     || (parseInt(o.responseTime) || 99) <= 3;
      const matchChipVer = !chips.verified || o.user?.verified === true;
      const matchOnline  = !chips.online   || traderPresence(o).online;
      const matchFav     = !chips.fav      || favTraders.has(o.trader);

      return matchSearch && matchType && matchChain && matchPay && matchCrypto && matchCountry &&
             matchRating && matchTrades && matchResponseTime && matchLevel && matchVerified && matchAmount &&
             matchStable && matchTrm && matchFast && matchChipVer && matchOnline && matchFav;
    });

    // Apply sorting with direction
    if (sortBy === 'spread') {
      result = [...result].sort((a, b) => {
        const diff = (parseFloat(a.spread)||0) - (parseFloat(b.spread)||0);
        return sortDir === 'desc' ? -diff : diff;
      });
    }
    if (sortBy === 'speed') {
      result = [...result].sort((a, b) => {
        const diff = (parseInt(a.responseTime)||99) - (parseInt(b.responseTime)||99);
        return sortDir === 'desc' ? -diff : diff;
      });
    }
    if (sortBy === 'trust') {
      result = [...result].sort((a, b) => {
        const diff = getTrustScore(b) - getTrustScore(a);
        return sortDir === 'desc' ? diff : -diff;
      });
    }
    if (sortBy === 'volume') {
      result = [...result].sort((a, b) => {
        const diff = (parseFloat(b.amount)||0) - (parseFloat(a.amount)||0);
        return sortDir === 'desc' ? diff : -diff;
      });
    }
    return result;
  }, [search, amount, typeFilter, chainFilter, payFilter, cryptoFilter, countryFilter, sortBy, sortDir, advFilters, allOffers, chips, favTraders]);

  const paginated  = filtered.slice(0, page * PAGE_SIZE);   // feed acumulativo (cargar más / scroll infinito)
  const hasMore    = paginated.length < filtered.length;

  // Scroll infinito: carga el siguiente bloque al acercarse al final.
  useEffect(() => {
    if (!hasMore) return undefined;
    const el = loadMoreRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setPage((p) => p + 1);
    }, { rootMargin: '320px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, page, filtered.length]);
  const bestOfferId = paginated.length ? paginated.reduce((b, o) => (parseFloat(o.spread) || 0) < (parseFloat(b.spread) || 0) ? o : b, paginated[0]).id : null;
  // Anuncio destacado: el de mejor reputación del set filtrado (verificado, rating + trades altos)
  const promotedOffer = (() => {
    if (filtered.length < 3) return null;
    const feat = filtered.filter(o => parseFloat(o.rating) >= 4.8 && parseInt(o.trades) >= 100);
    const pool = feat.length ? feat : filtered;
    return pool.reduce((b, o) => getTrustScore(o) > getTrustScore(b) ? o : b, pool[0]);
  })();
  const handleCountryChange = (val) => { setCountry(val); setPay('Todos'); setPage(1); };
  const handleFilter = (setter, val) => { setter(val); setPage(1); };

  // Handle header clicks for sorting
  const handleHeaderSort = (column) => {
    const sortMap = {
      'precio': 'spread',
      'resp': 'speed',
      'confianza': 'trust',
      'volumen': 'volume',
      'trades': 'volume',
    };
    const newSort = sortMap[column] || 'default';

    // Toggle direction if clicking same column, otherwise set to 'asc'
    if (sortBy === newSort) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSort);
      setSortDir('asc');
    }
  };

  return (
    <div className={`mk-wrapper ${mainTab === 'market' ? 'show-market' : 'show-offers'}`}>
      {successToast && (
        <div style={{ background: '#00b248', color: '#fff', padding: '12px 20px', borderRadius: '10px', marginBottom: '12px', fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {successToast}
        </div>
      )}

      {/* ✅ TABS PRINCIPALES */}
      <div className="mk-main-tabs">
        <button className={`mk-main-tab ${mainTab === 'offers' ? 'active' : ''}`} onClick={() => goTab('offers')}><FiUsers className="mk-tab-ic" /> P2P Marketplace</button>
        <button className={`mk-main-tab ${mainTab === 'market' ? 'active' : ''}`} onClick={() => goTab('market')}><FiRadio className="mk-tab-ic" /> Blockchain Live Price</button>
        <button className={`mk-main-tab ${mainTab === 'portfolio' ? 'active' : ''}`} onClick={() => goTab('portfolio')}><FiPieChart className="mk-tab-ic" /> Portafolio</button>
        <button className={`mk-main-tab ${mainTab === 'community' ? 'active' : ''}`} onClick={() => goTab('community')}><FiMessageCircle className="mk-tab-ic" /> Muro</button>
      </div>

      {/* TAB MERCADO */}
      {mainTab === 'market' && <CryptoMarket onTrade={handleTradeFromMarket} />}
      {mainTab === 'portfolio' && <Portfolio />}
      {mainTab === 'community' && <CommunityFeed offers={allOffers} onViewAsset={handleTradeFromMarket} onNavigate={handleNavigateToTrader} />}

      {/* TAB OFERTAS */}
      {mainTab === 'offers' && (
        <>
          <div className="mk-hero">
            <h1 className="mk-hero-title">{typeFilter === 'sell' ? 'Vendé' : 'Comprá'} cripto <span className="mk-hero-accent">a tu precio</span></h1>
            <p className="mk-hero-sub">Ningún exchange te dicta el precio: lo acordás con tu contraparte. Vos sos el mercado.</p>
            <div className="mk-hero-meta">
              <span className="mk-hero-rate">1 USDT ≈ 1 USD</span>
              <span className="mk-hero-dot">·</span>
              <span><span className="fin-num">{filtered.length}</span> ofertas activas</span>
            </div>
            <div className="mk-hero-trust">
              <FiShield className="mk-hero-trust-ic" />
              <span><strong>Vendedores verificados:</strong> solo comerciantes aprobados por invitación pueden vender. Comprá con confianza.</span>
              <Link to="/security" className="mk-hero-trust-link">Por qué es seguro →</Link>
            </div>
          </div>

          {eduOpen && (
            <div className="mk-edu">
              <button className="mk-edu-close" onClick={dismissEdu} aria-label="Cerrar">✕</button>
              <div className="mk-edu-main">
                <div className="mk-edu-stable">
                  <span className="mk-edu-coins"><CoinLogo symbol="USDT" size={30} /><CoinLogo symbol="USDC" size={30} /></span>
                  <div>
                    <strong>Negociá en dólares estables (USDT / USDC)</strong>
                    <p>Una <em>stablecoin</em> vale siempre ≈ $1: no sube ni baja como Bitcoin. Es la forma más simple y predecible de guardar y enviar dólares. <button type="button" className="mk-edu-link" onClick={() => setEduCenter('glos')}>Aprender más →</button></p>
                  </div>
                </div>
                <div className="mk-edu-steps">
                  <span className="mk-edu-steps-title"><FiHelpCircle /> ¿Cómo funciona el P2P?</span>
                  <ol>
                    <li><span className="mk-edu-num">1</span> Elegís una oferta y tu método de pago</li>
                    <li><span className="mk-edu-num">2</span> Pagás por tu método y marcás el pago</li>
                    <li><span className="mk-edu-num">3</span> El vendedor confirma y recibís tu cripto en tu wallet</li>
                  </ol>
                  <button type="button" className="mk-edu-link mk-edu-guide-link" onClick={() => setEduCenter('estafa')}>Guía completa y cómo evitar estafas →</button>
                </div>
              </div>
            </div>
          )}

          <div className="mk-filters-card">
            <div className="mk-filters-top">
              <div className="mk-search-wrap">
                <FiSearch className="mk-search-icon" />
                <input className="mk-search" placeholder="Buscar trader..." value={search} onChange={e => handleFilter(setSearch, e.target.value)} />
              </div>
              <div className="mk-type-tabs">
                {[
                  { key: 'all', label: 'Todas', icon: FiGlobe },
                  { key: 'buy', label: 'Compras', icon: FiShoppingCart },
                  { key: 'sell', label: 'Ventas', icon: FiDollarSign }
                ].map(t => {
                  const Icon = t.icon;
                  return (
                    <button key={t.key} className={`mk-type-tab mk-type-${t.key} ${typeFilter === t.key ? 'active' : ''}`} onClick={() => handleFilter(setType, t.key)}>
                      <Icon size={16} style={{ marginRight: '6px' }} />
                      {t.label}
                    </button>
                  );
                })}
              </div>
              <div className="mk-amount-wrap">
                <input className="mk-amount-input fin-num" type="number" min="0" placeholder="Monto" value={amount} onChange={e => handleFilter(setAmount, e.target.value)} />
                <span className="mk-amount-cur">USD</span>
              </div>
              <div className="mk-amount-presets">
                {['100', '500', '1000'].map(v => (
                  <button key={v} type="button" className={`mk-preset ${amount === v ? 'active' : ''}`} onClick={() => handleFilter(setAmount, amount === v ? '' : v)}>${Number(v).toLocaleString()}</button>
                ))}
              </div>
            </div>
            <div className="mk-filters-row">
              <CountrySelect value={countryFilter} onChange={handleCountryChange} />
              <select className="mk-select" value={payFilter} onChange={e => handleFilter(setPay, e.target.value)}>
                <option value="Todos">Método de pago</option>
                {groupPayments(availablePayments).map(([kind, items]) => (
                  <optgroup key={kind} label={PAY_KIND_LABEL[kind] || kind}>
                    {items.map(p => <option key={p} value={p}>{p}</option>)}
                  </optgroup>
                ))}
              </select>
              <IconSelect value={chainFilter} onChange={v => handleFilter(setChain, v)} options={CHAIN_OPTIONS} />
              <IconSelect value={cryptoFilter} onChange={v => handleFilter(setCrypto, v)} options={CRYPTO_OPTIONS} />
              <select className="mk-select" value={sortBy} onChange={e => handleFilter(setSortBy, e.target.value)}>
                {SORT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            {/* CHIPS RÁPIDOS */}
            <div className="mk-quickchips">
              <span className="mk-quickchips-label">Rápido:</span>
              <button type="button" className={`mk-qchip ${chips.stable ? 'active' : ''}`} onClick={() => toggleChip('stable')}>
                <CoinLogo symbol="USDT" size={15} /> Solo estables
              </button>
              <button type="button" className={`mk-qchip ${chips.trm ? 'active' : ''}`} onClick={() => toggleChip('trm')}>Sin recargo (TRM)</button>
              <button type="button" className={`mk-qchip ${chips.fast ? 'active' : ''}`} onClick={() => toggleChip('fast')}><FiZap className="mk-qchip-ic" /> Rápidos</button>
              <button type="button" className={`mk-qchip ${chips.online ? 'active' : ''}`} onClick={() => toggleChip('online')}><span className="mk-qchip-online" /> En línea</button>
              <button type="button" className={`mk-qchip ${chips.fav ? 'active' : ''}`} onClick={() => toggleChip('fav')}><FiStar className="mk-qchip-ic" /> Favoritos</button>
              <button type="button" className={`mk-qchip ${chips.verified ? 'active' : ''}`} onClick={() => toggleChip('verified')}><FiShield className="mk-qchip-ic" /> Verificados</button>
              <button type="button" className={`mk-qchip ${sortBy === 'spread' ? 'active' : ''}`} onClick={() => { handleFilter(setSortBy, 'spread'); setSortDir('asc'); }}><FiTrendingUp className="mk-qchip-ic" /> Mejor precio</button>
            </div>

            {cryptoFilter !== 'Todas' && !isStable(cryptoFilter) && (
              <div className="mk-volatile-nudge">
                <FiAlertTriangle className="mk-vn-ic" />
                <span>Estás viendo <strong>{cryptoFilter}</strong>, una cripto <strong>volátil</strong>: su precio cambia a cada rato. Si solo querés guardar o enviar valor, USDT/USDC son más predecibles.</span>
                <button type="button" className="mk-vn-btn" onClick={() => handleFilter(setCrypto, 'USDT')}>Cambiar a USDT</button>
              </div>
            )}

            {/* ADVANCED FILTERS */}
            <AdvancedFilters
              filters={advFilters}
              onFiltersChange={setAdvFilters}
              matchingCount={filtered.length}
              totalCount={allOffers.length}
            />

            {countryFilter !== 'all' && (
              <div className="mk-active-country">
                <span><Flag code={countryFilter} /> Mostrando ofertas de <strong>{getCountry(countryFilter).label}</strong> · {availablePayments.length - 1} métodos disponibles</span>
                <button className="mk-clear-country" onClick={() => handleCountryChange('all')}>✕ Ver global</button>
              </div>
            )}
            <div className="mk-results-bar">
              <span className="mk-results-count"><span className="fin-num">{filtered.length}</span> ofertas encontradas {apiOffers.length > 0 && <span style={{ color: 'var(--fin-up)', fontSize: '0.75rem' }}>· <span className="fin-num">{apiOffers.length}</span> reales</span>}</span>
              <span className="mk-results-hint">Precio libre — el mercado decide el valor justo</span>
            </div>
          </div>

          {!user && (
            <div className="mk-cta-banner">
              <div className="mk-cta-text">
                <strong>Dejá de tomar el precio que te dan.</strong>
                <span>Creá tu cuenta gratis y operá a tu precio, de persona a persona.</span>
              </div>
              <button className="mk-cta-btn" onClick={() => openAuth('register')}>Crear cuenta</button>
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="mk-empty">
              <FiSearch className="mk-empty-icon" />
              <p className="mk-empty-title">No hay ofertas con esos filtros</p>
              <p className="mk-empty-sub">Probá ampliar el monto o quitar algún filtro.</p>
              <button className="mk-empty-btn" onClick={() => { setSearch(''); setType('all'); setPay('Todos'); setChain('Todas'); setCrypto('Todas'); setAmount(''); handleCountryChange('all'); setPage(1); }}>Limpiar filtros</button>
            </div>
          ) : (
            <>
              <PromotedBanner offer={promotedOffer} onBuy={handleBuyGated} onOffer={handleOfferGated} onNavigate={handleNavigateToTrader} isVerified={isVerified} amount={amount} fiatRates={fiatRates} parRates={parRates} />
              <div className="mk-table-wrap">
                <table className="mk-table">
                  <thead>
                    <tr>
                      <th className={`sortable ${sortBy === 'trust' ? `sort-${sortDir}` : ''}`} onClick={() => handleHeaderSort('confianza')}>Anunciante <InfoTip text={EDU.trader} /></th>
                      <th className={`sortable ${sortBy === 'spread' ? `sort-${sortDir}` : ''}`} onClick={() => handleHeaderSort('precio')}>Precio <InfoTip text={EDU.price} /></th>
                      <th>Disponible · Límite <InfoTip text={EDU.available} /></th>
                      <th>Pago <InfoTip text={EDU.payment} /></th>
                      <th className="mk-th-trade">Trade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map(o => (
                      <OfferRow key={o.id} offer={o} onOffer={handleOfferGated} onBuy={handleBuyGated} onShare={setShareModal} onNavigate={handleNavigateToTrader} isVerified={isVerified} amount={amount} best={o.id === bestOfferId} fiatRates={fiatRates} parRates={parRates} />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mk-cards-wrap">
                {paginated.map(o => (
                  <OfferCard key={o.id} offer={o} onOffer={handleOfferGated} onBuy={handleBuyGated} onShare={setShareModal} onNavigate={handleNavigateToTrader} isVerified={isVerified} amount={amount} best={o.id === bestOfferId} fiatRates={fiatRates} parRates={parRates} />
                ))}
              </div>
              {hasMore ? (
                <div className="mk-loadmore">
                  <button className="mk-loadmore-btn" onClick={() => setPage(p => p + 1)}>
                    Cargar más ofertas
                    <span className="mk-loadmore-rest">+{Math.min(PAGE_SIZE, filtered.length - paginated.length)}</span>
                  </button>
                  <div className="mk-loadmore-info">
                    Mostrando <span className="fin-num">{paginated.length}</span> de <span className="fin-num">{filtered.length}</span> ofertas
                  </div>
                  <div ref={loadMoreRef} className="mk-scroll-sentinel" aria-hidden="true" />
                </div>
              ) : (
                filtered.length > PAGE_SIZE && (
                  <div className="mk-loadmore mk-loadmore-done">
                    <span className="mk-loadmore-info">✓ Estás viendo las <span className="fin-num">{filtered.length}</span> ofertas</span>
                  </div>
                )
              )}
            </>
          )}
        </>
      )}

      {offerModal && <OfferModal offer={offerModal} onClose={() => setOfferModal(null)} isVerified={isVerified} />}
      {shareModal && <ShareModal offer={shareModal} onClose={() => setShareModal(null)} />}
      {eduCenter && <EduCenter initialTab={eduCenter} onClose={() => setEduCenter(null)} />}
    </div>
  );
}
